import React, { useState, useEffect } from 'react';
import Alert from '@mui/material/Alert';
import CloseIcon from '@mui/icons-material/Close';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Card,
  CardContent,
  CardHeader,
  Typography,
  TextField,
  Button,
  CircularProgress,
  Stack,
  Divider,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  IconButton,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Autocomplete,
  FormControlLabel,
  Checkbox,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Snackbar,
  ToggleButton,
  ToggleButtonGroup
} from '@mui/material';
import { 
  Save, 
  ArrowBack, 
  Add, 
  Delete, 
  Business, 
  Person, 
  Home, 
  LocalShipping, 
  Build,
  Receipt,
  ExpandMore,
  Edit,
  CheckCircle,
  Cancel
} from '@mui/icons-material';
import { 
  BudgetCreate, 
  BudgetVersionCreate, 
  EnvironmentCreate, 
  EnvironmentMaterialCreate, 
  FreightCreate,
  EnvironmentInstallationCreate,
  Product,
  InstallationService,
  TipoInstalacion
} from '../../types';
import { BudgetApiService } from '../../services/budgetApiService';
import { apiService } from '../../services/apiService';
import { useAuth } from '../../context/AuthContext';

// Constante para el ambiente de instalaciones generales (oculto en materiales)
const GENERAL_INSTALLATIONS_ENV_NAME = '__INSTALACIONES_GENERALES__';
const GENERAL_INSTALLATIONS_INDEX = -1;

interface EnvironmentFormData {
  nombre: string;
  descripcion: string;
  orden: number;
  descuento_materiales: number;
  materials: MaterialFormData[];
  installations: InstallationFormData[];
}

interface MaterialFormData {
  // Producto existente o nuevo
  product_id?: number;
  product?: Product;
  es_in_situ: boolean;
  
  // Datos del material
  descripcion: string;
  codigo: string;
  unidades: number;
  precio_unitario: number;
  currency: 'USD' | 'ARS';
  
  // Datos de cálculo
  metros_cuadrados?: number;
  cantidad_calculada?: number;
  cantidad_manual?: number;
  usar_cantidad_manual?: boolean;
  
  // Opciones de producto
  con_desperdicio?: boolean;
  con_estructura?: boolean;
  
  // Observaciones
  observaciones?: string;
}

interface InstallationFormData {
  installation_service_id?: number;
  installation_service?: InstallationService;
  tipo_instalacion?: TipoInstalacion;
  cantidad_manual: number;
  trabajo?: string;
  descripcion_trabajo?: string;
  mano_obra_m2?: number;
  unidad?: string;  // Unidad para instalaciones custom
  currency: 'USD' | 'ARS';
}

interface FreightFormData {
  tipo_envio: string;
  descripcion_detalle?: string;
  monto: number;
  tipo: 'fijo' | 'porcentaje';
  currency: 'USD' | 'ARS';
}

interface BudgetFormData extends BudgetCreate {
  // Datos de la primera versión
  version_titulo: string;
  version_numero: string;
  vendedor: string;
  email_vendedor: string;
  tel_vendedor: string;
  condiciones_contratacion: string;
  condiciones_adicionales: string;
  descuento_materiales: number;
  descuento_instalacion: number;
  descuento_fletes: number;
  
  // Datos de ambientes, fletes, etc.
  environments: EnvironmentFormData[];
  freights: FreightFormData[];
}

const BudgetFormNew: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth(); // Obtener el usuario autenticado
  
  // Estados para productos
  const [products, setProducts] = useState<Product[]>([]);
  
  // Estados principales
  const [formData, setFormData] = useState<BudgetFormData>({
    proyecto: '',
    cliente: '',
    responsable: '',
    email_cliente: '',
    tel_cliente: '',
    version_titulo: '', // Ahora empieza vacío, y si no se llena se utilizará el valor de "proyecto" 
    version_numero: '1.0', // Se mantiene por compatibilidad pero ya no se usa (lo genera el backend)
    vendedor: user?.name || '',
    email_vendedor: user?.email || '',
    tel_vendedor: user?.telefono || '',
    condiciones_contratacion: 'Presupuesto válido por 7 días\nPlazo de entrega: A convenir.\nValores expresados en dólares USD: cotización Dólar Oficial tipo vendedor Banco Nación. https://www.bna.com.ar/Personas\nLa factura se emite en pesos argentinos\nForma de pago: anticipado\n\nFormas de pago materiales:\n•⁠  ⁠Contado efectivo o Transferencia Bancaria\n•⁠  ⁠Tarjeta 6 cuotas: Pago del 32% adicional y 6 cuotas fijas en pesos\n•⁠  ⁠Tarjeta 12 cuotas: Pago del 70% adicional y 12 cuotas fijas en pesos\n•⁠  ⁠Tarjeta de credito del Banco Galicia: 3 cuotas sin interes pagando los miércoles o viernes',
    condiciones_adicionales: 'Presupuesto sujeto a modificaciones, luego de Visita Técnica a obra o al inicio de la misma.\nEn caso de que durante el desarrollo de la obra se precise más mercadería, el cliente deberá abonar los materiales restantes y asumir el costo de mano de obra adicional.\nLa colocación la realizan equipos terciarizados certificados y auditados por LifeCycle\nMetodología de pago de mano de obra: 50% previo al comienzo de los trabajos y 50% una vez finalizados los trabajos, previa conformidad del cliente.\nEl presupuesto de mano de obra se ajustará por IPC\nEl presupuesto de colocación no incluye andamios ni volquetes\nEl flete no esta incluido en el presupuesto, el mismo se coordinará una vez confirmada la fecha y lugar de entrega.',
    descuento_materiales: 0,
    descuento_instalacion: 0,
    descuento_fletes: 0,
    environments: [],
    freights: []
  });
  
  // Estados de UI
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [errors, setErrors] = useState<Record<string, string>>({});
  
  // Estados para diálogos
  const [environmentDialog, setEnvironmentDialog] = useState(false);
  const [materialDialog, setMaterialDialog] = useState(false);
  const [freightDialog, setFreightDialog] = useState(false);
  const [installationDialog, setInstallationDialog] = useState(false);
  const [showCondiciones, setShowCondiciones] = useState(false);
  
  // Estados para formularios de diálogos
  const [selectedEnvironmentIndex, setSelectedEnvironmentIndex] = useState<number | null>(null);
  
  // Estados para installation services
  const [installationServices, setInstallationServices] = useState<InstallationService[]>([]);
  const [loadingInstallationServices, setLoadingInstallationServices] = useState(false);
  const [newEnvironment, setNewEnvironment] = useState<EnvironmentFormData>({
    nombre: '',
    descripcion: '',
    orden: 1, // Se mantendrá aunque no se muestre en el formulario
    descuento_materiales: 0,
    materials: [],
    installations: []
  });
  const [newMaterial, setNewMaterial] = useState<MaterialFormData>({
    descripcion: '',
    codigo: '',
    unidades: 0,
    precio_unitario: 0,
    currency: 'USD',
    es_in_situ: false,
    metros_cuadrados: 1,
    usar_cantidad_manual: false,
    con_desperdicio: true, // Preseleccionado por defecto
    con_estructura: false,
    observaciones: ''
  });
  const [newFreight, setNewFreight] = useState<FreightFormData>({
    tipo_envio: '',
    monto: 0,
    tipo: 'fijo',
    currency: 'ARS'
  });
  const [newInstallation, setNewInstallation] = useState<InstallationFormData>({
    cantidad_manual: 1,
    currency: 'ARS'
  });
  const [installationMode, setInstallationMode] = useState<'catalog' | 'custom'>('catalog');
  const [alertStock, setAlertStock] = useState<string | null>(null);
  const [showAlertStock, setShowAlertStock] = useState(false);
  
  // Estado para edición de nombres de ambientes (solo local, no persiste hasta guardar)
  const [editingEnvironmentIndex, setEditingEnvironmentIndex] = useState<number | null>(null);
  const [editingEnvironmentName, setEditingEnvironmentName] = useState<string>('');
  
  // Estado para el input de descuento global (solo para escritura)
  const [globalDiscountInput, setGlobalDiscountInput] = useState<string>('');
  
  // Calcular si todos los ambientes tienen el mismo descuento
  const getCommonDiscount = (): number | null => {
    const envs = formData.environments.filter(e => e.nombre !== GENERAL_INSTALLATIONS_ENV_NAME);
    if (envs.length === 0) return null;
    const firstDiscount = envs[0].descuento_materiales || 0;
    const allSame = envs.every(env => (env.descuento_materiales || 0) === firstDiscount);
    return allSame ? firstDiscount : null;
  };
  
  const commonDiscount = getCommonDiscount();
  
  // Actualizar el input cuando los descuentos individuales cambien y sean uniformes
  useEffect(() => {
    if (commonDiscount !== null) {
      setGlobalDiscountInput(Math.round(commonDiscount).toString());
    } else {
      setGlobalDiscountInput('');
    }
  }, [commonDiscount]);
  
  // Función para aplicar descuento global a todos los ambientes
  const applyGlobalDiscount = () => {
    const value = parseFloat(globalDiscountInput);
    if (isNaN(value) || value < 0 || value > 100) return;
    
    setFormData(prev => ({
      ...prev,
      environments: prev.environments.map(env => ({
        ...env,
        descuento_materiales: value
      }))
    }));
  };

  // Cargar productos al inicio
  useEffect(() => {
    loadProducts();
    loadInstallationServices();
    loadConditions();
  }, []);

  // Cargar condiciones desde la API
  const loadConditions = async () => {
    try {
      const response = await apiService.get<{ condiciones_generales_default: string; condiciones_adicionales_default: string }>('/app-settings/conditions');
      setFormData(prev => ({
        ...prev,
        condiciones_contratacion: response.condiciones_generales_default || prev.condiciones_contratacion,
        condiciones_adicionales: response.condiciones_adicionales_default || prev.condiciones_adicionales
      }));
    } catch (error) {
      console.error('Error loading conditions from app settings:', error);
      // Si falla, se mantienen los valores por defecto
    }
  };

  // Cargar installation services
  const loadInstallationServices = async () => {
    try {
      setLoadingInstallationServices(true);
      // Crear token header manualmente como hace el resto de la app
      const token = localStorage.getItem('token');
      const headers: Record<string, string> = {
        'Content-Type': 'application/json'
      };
      if (token) {
        headers.Authorization = `Bearer ${token}`;
      }
      
  const response = await fetch(`/api/installation-services?is_active=true&limit=100`, {
        method: 'GET',
        headers
      });
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const services: InstallationService[] = await response.json();
      setInstallationServices(Array.isArray(services) ? services : []);
    } catch (error) {
      console.error('Error loading installation services:', error);
      setError('Error al cargar servicios de instalación');
    } finally {
      setLoadingInstallationServices(false);
    }
  };

  // Actualizar datos del vendedor cuando cambia el usuario
  useEffect(() => {
    if (user) {
      setFormData(prev => ({
        ...prev,
        vendedor: user.name || '',
        email_vendedor: user.email || '',
        tel_vendedor: user.telefono || ''
      }));
    }
  }, [user]);

  const loadProducts = async () => {
    try {
      const productsData = await apiService.products.getAll({ active_only: true, limit: 1000 });
      setProducts(Array.isArray(productsData) ? productsData : productsData.items || []);
    } catch (err) {
      console.error('Error loading products:', err);
    }
  };

  // Validar formulario
  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};
    
    if (!formData.proyecto.trim()) {
      newErrors.proyecto = 'El nombre del proyecto es requerido';
    }
    
    if (!formData.cliente.trim()) {
      newErrors.cliente = 'El nombre del cliente es requerido';
    }
    
    // Título de versión ya no es requerido porque se puede usar proyecto como valor por defecto
    
    if (!formData.vendedor.trim()) {
      newErrors.vendedor = 'El nombre del vendedor es requerido';
    }
    
    if (formData.email_cliente && !/\S+@\S+\.\S+/.test(formData.email_cliente)) {
      newErrors.email_cliente = 'El email no tiene un formato válido';
    }
    
    if (formData.email_vendedor && !/\S+@\S+\.\S+/.test(formData.email_vendedor)) {
      newErrors.email_vendedor = 'El email no tiene un formato válido';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Manejar cambios en los campos
  const handleChange = (field: keyof BudgetFormData) => (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setFormData(prev => ({
      ...prev,
      [field]: event.target.value
    }));
    
    // Limpiar error del campo
    if (errors[field]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
  };

  // Funciones para manejar ambientes
  const handleAddEnvironment = () => {
    if (!newEnvironment.nombre.trim()) return;
    
    setFormData(prev => ({
      ...prev,
      environments: [...prev.environments, { ...newEnvironment, orden: prev.environments.length + 1 }]
    }));
    
    setNewEnvironment({
      nombre: '',
      descripcion: '',
      orden: 1, // Mantenemos el valor por defecto aunque no se muestre en el formulario
      descuento_materiales: 0,
      materials: [],
      installations: []
    });
    setEnvironmentDialog(false);
  };

  const handleDeleteEnvironment = (index: number) => {
    setFormData(prev => ({
      ...prev,
      environments: prev.environments.filter((_, i) => i !== index)
    }));
  };

  // Funciones de edición de nombre de ambiente (solo actualiza estado local)
  const handleStartEditEnvironmentName = (envIndex: number, currentName: string) => {
    setEditingEnvironmentIndex(envIndex);
    setEditingEnvironmentName(currentName);
  };

  const handleCancelEditEnvironmentName = () => {
    setEditingEnvironmentIndex(null);
    setEditingEnvironmentName('');
  };

  const handleSaveEnvironmentName = (envIndex: number) => {
    // Validar nombre no vacío
    const trimmedName = editingEnvironmentName.trim();
    if (!trimmedName) {
      setError('El nombre del ambiente no puede estar vacío');
      return;
    }

    // Actualizar estado local
    setFormData(prev => ({
      ...prev,
      environments: prev.environments.map((env, idx) =>
        idx === envIndex ? { ...env, nombre: trimmedName } : env
      )
    }));

    // Limpiar estado de edición
    setEditingEnvironmentIndex(null);
    setEditingEnvironmentName('');
  };

  // Funciones para manejar materiales
  const handleAddMaterial = () => {
    if (selectedEnvironmentIndex === null) return;
    
    // Validar según si es producto existente o in situ
    if (!newMaterial.es_in_situ) {
      if (!newMaterial.product_id) return;
    } else {
      if (!newMaterial.codigo.trim() || !newMaterial.descripcion.trim()) return;
    }

    // Verificar stock antes de agregar
    if (newMaterial.product && typeof newMaterial.product.stock === 'number') {
      const cantidadPresupuestada = newMaterial.unidades;
      if (cantidadPresupuestada > newMaterial.product.stock) {
        setAlertStock(
          `Estás por presupuestar más de lo que hay en stock (${newMaterial.product.stock}). Revisa los próximos ingresos.`
        );
        // No hacer return, solo mostrar la alerta
      } else {
        setAlertStock(null);
      }
    }
    
    // Calcular cantidad final
    let cantidadFinal = newMaterial.unidades;
    if (!newMaterial.es_in_situ && newMaterial.product && newMaterial.metros_cuadrados) {
      if (newMaterial.usar_cantidad_manual && newMaterial.cantidad_manual) {
        cantidadFinal = newMaterial.cantidad_manual;
      } else if (newMaterial.cantidad_calculada) {
        cantidadFinal = newMaterial.cantidad_calculada;
      }
    }


    let materialToAdd: MaterialFormData = {
      ...newMaterial,
      unidades: cantidadFinal,
      currency: newMaterial.currency || 'USD'
    };

    // Si el material es de grupo ESTRUCTURA, forzar flags y observaciones igual que los auto-agregados
    if (newMaterial.product && newMaterial.product.grupo === 'ESTRUCTURA') {
      materialToAdd = {
        ...materialToAdd,
        metros_cuadrados: undefined,
        con_estructura: false,
        con_desperdicio: false,
        usar_cantidad_manual: false,
        observaciones: 'Estructura para ', // código vacío
      };
    }

    // Preparar lista de materiales a agregar (incluye el material principal)
    const materialsToAdd: MaterialFormData[] = [materialToAdd];

    // Si tiene estructura habilitada, procesar y agregar materiales de estructura
    if (newMaterial.con_estructura && newMaterial.product?.estructura_m2) {
      const cantidadBase = (newMaterial.product.grupo === 'PERFIL')
        ? (newMaterial.unidades || 1)
        : (newMaterial.metros_cuadrados || 1);
      const structureMaterials = processStructureMaterials(
        newMaterial.product.estructura_m2,
        cantidadBase,
        newMaterial.con_desperdicio || false,
        newMaterial.product.codigo
      );
      materialsToAdd.push(...structureMaterials);
    }

    // Agregar todos los materiales al ambiente
    setFormData(prev => {
      const newEnvironments = [...prev.environments];
      newEnvironments[selectedEnvironmentIndex].materials.push(...materialsToAdd);
      return { ...prev, environments: newEnvironments };
    });

    setNewMaterial({
      descripcion: '',
      codigo: '',
      unidades: 0,
      precio_unitario: 0,
      currency: 'USD',
      es_in_situ: false,
      metros_cuadrados: 1,
      usar_cantidad_manual: false,
      con_desperdicio: true,
      con_estructura: false,
      observaciones: ''
    });
    setMaterialDialog(false);
    setSelectedEnvironmentIndex(null);
  };

  // Función para calcular cantidad automáticamente basada en m²
  const calculateMaterialQuantity = (product: Product, metros_cuadrados: number, con_desperdicio: boolean = false): number => {
    if (!product.largo || !product.ancho) return 1;
    
    // Usar ancho_util si existe (para considerar solapamiento), sino usar ancho normal
    const ancho_efectivo = product.ancho_util ?? product.ancho;
    
    // Las dimensiones en la BD ya están en metros, no necesitan conversión
    const area_por_unidad = product.largo * ancho_efectivo; // m² útiles
    
    // Si el área es 0, asumir 1 pieza por m²
    if (area_por_unidad === 0) {
      return Math.ceil(metros_cuadrados);
    }
    
    // Calcular cuántas unidades necesito para cubrir los metros cuadrados
    let cantidad = Math.ceil(metros_cuadrados / area_por_unidad);
    
    // Aplicar desperdicio (10% adicional)
    if (con_desperdicio) {
      cantidad = Math.ceil(cantidad * 1.1);
    }
    
    return cantidad;
  };

  // Función para calcular m² basado en cantidad de unidades
  const calculateMetrosCuadradosFromQuantity = (product: Product, cantidad: number): number => {
    if (!product.largo || !product.ancho || cantidad <= 0) return 0;
    
    // Usar ancho_util si existe (para considerar solapamiento), sino usar ancho normal
    const ancho_efectivo = product.ancho_util ?? product.ancho;
    
    // Las dimensiones en la BD ya están en metros
    const area_por_unidad = product.largo * ancho_efectivo; // m² útiles
    
    // Calcular metros cuadrados totales
    return cantidad * area_por_unidad;
  };

  // Función para procesar materiales de estructura
  const processStructureMaterials = (estructura_m2: string, metros_cuadrados: number, con_desperdicio: boolean, productoPrincipalCodigo: string): MaterialFormData[] => {
    if (!estructura_m2?.trim()) return [];
    
    const structureMaterials: MaterialFormData[] = [];
    
    // Parsear estructura_m2 (formato: "codigo1,cantidad_por_m2;codigo2,cantidad_por_m2")
    const entries = estructura_m2.split(/[;\n]+/);
    
    for (const entry of entries) {
      const trimmedEntry = entry.trim();
      if (!trimmedEntry) continue;
      
      try {
        const [codigo, cantidadPorM2Str] = trimmedEntry.split(',');
        const codigoTrimmed = codigo.trim();
        const cantidadPorM2 = parseFloat(cantidadPorM2Str.trim());
        
        // Buscar el producto por código
        const structureProduct = products.find((p: Product) => p.codigo === codigoTrimmed);
        if (!structureProduct) {
          console.warn(`Producto de estructura no encontrado: ${codigoTrimmed}`);
          continue;
        }
        
        // Calcular cantidad total: cantidad_por_m2 * metros_cuadrados
        let cantidad = cantidadPorM2 * metros_cuadrados;
        
        // NOTA: El desperdicio NO se aplica a los materiales de estructura
        // Solo se redondea hacia arriba para obtener un número entero de unidades
        cantidad = Math.ceil(cantidad);
        
        structureMaterials.push({
          descripcion: structureProduct.codigo,
          codigo: structureProduct.codigo,
          unidades: cantidad,
          precio_unitario: structureProduct.precio_unitario || 0,
          currency: 'USD',
          es_in_situ: false,
          product_id: structureProduct.id,
          product: structureProduct,
          metros_cuadrados: undefined, // ✅ Items de estructura NO tienen m2
          usar_cantidad_manual: false,
          con_desperdicio: false, // Los materiales de estructura NO tienen desperdicio
          con_estructura: false, // Los materiales de estructura no tienen estructura adicional
          observaciones: `Estructura para ${productoPrincipalCodigo}`
        });
      } catch (error) {
        console.warn('Error procesando entrada de estructura:', entry, error);
        continue;
      }
    }
    
    return structureMaterials;
  };  

  
  // Función para manejar selección de producto
  const handleProductSelect = (product: Product | null) => {
    if (!product) {
      setNewMaterial(prev => ({
        ...prev,
        product_id: undefined,
        product: undefined,
        descripcion: '',
        codigo: '',
        precio_unitario: 0,
        cantidad_calculada: 0
      }));
      return;
    }

    const cantidadCalculada = calculateMaterialQuantity(
      product, 
      newMaterial.metros_cuadrados || 1, 
      newMaterial.con_desperdicio || false
    );
    
    setNewMaterial(prev => ({
      ...prev,
      product_id: product.id,
      product: product,
      descripcion: product.codigo,
      codigo: product.codigo,
      precio_unitario: product.precio_unitario || 0,
      cantidad_calculada: cantidadCalculada,
      unidades: prev.usar_cantidad_manual ? (prev.cantidad_manual || 0) : cantidadCalculada
    }));
  };

  // Función para manejar cambio en metros cuadrados
  const handleMetrosCuadradosChange = (metros: number) => {
    if (!newMaterial.product) return;
    
    const cantidadCalculada = calculateMaterialQuantity(
      newMaterial.product, 
      metros, 
      newMaterial.con_desperdicio || false
    );
    
    setNewMaterial(prev => ({
      ...prev,
      metros_cuadrados: metros,
      cantidad_calculada: cantidadCalculada,
      unidades: prev.usar_cantidad_manual ? (prev.cantidad_manual || 0) : cantidadCalculada
    }));
  };

  // Función para manejar cambio en cantidad manual
  const handleCantidadManualChange = (cantidad: number) => {
    if (!newMaterial.product) return;
    
    // Calcular metros cuadrados basado en la cantidad ingresada
    let cantidadBase = cantidad;
    
    // Si tiene desperdicio, necesitamos calcular la cantidad base (sin desperdicio)
    // para obtener los metros cuadrados reales que se quieren cubrir
    if (newMaterial.con_desperdicio) {
      cantidadBase = cantidad / 1.1; // Quitar el 10% de desperdicio
    }
    
    const metrosCalculados = calculateMetrosCuadradosFromQuantity(newMaterial.product, cantidadBase);
    
    setNewMaterial(prev => ({
      ...prev,
      cantidad_manual: cantidad,
      unidades: cantidad,
      metros_cuadrados: Math.round(metrosCalculados * 100) / 100 // Redondear a 2 decimales
    }));
  };

  // Función para manejar cambio en desperdicio
  const handleDesperdicioChange = (con_desperdicio: boolean) => {
    if (!newMaterial.product || !newMaterial.metros_cuadrados) return;
    
    const cantidadCalculada = calculateMaterialQuantity(
      newMaterial.product, 
      newMaterial.metros_cuadrados, 
      con_desperdicio
    );
    
    setNewMaterial(prev => ({
      ...prev,
      con_desperdicio,
      cantidad_calculada: cantidadCalculada,
      unidades: prev.usar_cantidad_manual ? (prev.cantidad_manual || 0) : cantidadCalculada
    }));
  };

  const handleDeleteMaterial = (envIndex: number, matIndex: number) => {
    setFormData(prev => {
      const newEnvironments = [...prev.environments];
      newEnvironments[envIndex].materials = newEnvironments[envIndex].materials.filter((_, i) => i !== matIndex);
      return { ...prev, environments: newEnvironments };
    });
  };

  // Funciones para manejar instalación
  const handleAddInstallation = () => {
    if (selectedEnvironmentIndex === null || 
        newInstallation.cantidad_manual <= 0) {
      return;
    }

    // Validar según el modo
    if (installationMode === 'catalog' && !newInstallation.installation_service_id) {
      return;
    }
    if (installationMode === 'custom' && (!newInstallation.trabajo?.trim() || !newInstallation.mano_obra_m2)) {
      return;
    }

    // Calcular subtotal - para unidad global no multiplicar por cantidad
    let precio = 0;
    if (installationMode === 'catalog') {
      precio = calculateInstallationPrice();
    } else {
      precio = newInstallation.mano_obra_m2 || 0;
    }
    const esGlobal = installationMode === 'custom' && newInstallation.unidad?.toLowerCase() === 'global';
    const subtotal = esGlobal ? precio : precio * newInstallation.cantidad_manual;

    // Determinar la unidad: para custom usar la seleccionada, para catálogo usar la del servicio
    let unidadFinal: string | undefined;
    if (installationMode === 'custom') {
      unidadFinal = newInstallation.unidad || '';
    } else if (newInstallation.installation_service) {
      unidadFinal = newInstallation.installation_service.unidad || '';
    }

    // Preparar la instalación con todos los campos necesarios
    const installationToAdd = {
      ...newInstallation,
      subtotal,
      mano_obra_m2: precio,
      unidad: unidadFinal,
      tipo_instalacion: installationMode === 'custom' ? 'custom' as const : newInstallation.tipo_instalacion
    };
    
    setFormData(prev => {
      const newEnvironments = [...prev.environments];
      
      // Si es el ambiente general (-1), buscarlo o crearlo
      if (selectedEnvironmentIndex === GENERAL_INSTALLATIONS_INDEX) {
        let generalEnvIndex = newEnvironments.findIndex(env => env.nombre === GENERAL_INSTALLATIONS_ENV_NAME);
        
        if (generalEnvIndex === -1) {
          // Crear el ambiente general
          newEnvironments.push({
            nombre: GENERAL_INSTALLATIONS_ENV_NAME,
            descripcion: 'Instalaciones generales del presupuesto',
            orden: 9999, // Al final
            descuento_materiales: 0,
            materials: [],
            installations: [installationToAdd]
          });
        } else {
          // Agregar al ambiente general existente
          newEnvironments[generalEnvIndex].installations = [
            ...newEnvironments[generalEnvIndex].installations,
            installationToAdd
          ];
        }
      } else {
        // Ambiente normal
        newEnvironments[selectedEnvironmentIndex].installations = [
          ...newEnvironments[selectedEnvironmentIndex].installations,
          installationToAdd
        ];
      }
      
      return { ...prev, environments: newEnvironments };
    });
    
    // Reset form
    setNewInstallation({
      cantidad_manual: 1,
      currency: 'ARS'
    });
    setInstallationMode('catalog');
    setInstallationDialog(false);
    setSelectedEnvironmentIndex(null);
  };

  // Manejar selección de installation service
  const handleInstallationServiceSelect = (service: InstallationService | null) => {
    if (!service) {
      setNewInstallation(prev => ({
        ...prev,
        installation_service_id: undefined,
        installation_service: undefined,
        tipo_instalacion: undefined,
        trabajo: undefined
      }));
      return;
    }

    // Calcular área del ambiente si hay uno seleccionado y la unidad es m²
    let cantidadInicial = 1;
    if (selectedEnvironmentIndex !== null && formData.environments[selectedEnvironmentIndex]) {
      const unidad = service.unidad?.toLowerCase() || '';
      if (unidad === 'm²' || unidad === 'm2' || unidad === 'm2') {
        const areaTotal = formData.environments[selectedEnvironmentIndex].materials.reduce(
          (total, mat) => total + (mat.metros_cuadrados || 0), 0
        );
        if (areaTotal > 0) {
          cantidadInicial = Math.round(areaTotal * 100) / 100; // Redondear a 2 decimales
        }
      }
    }

    setNewInstallation(prev => ({
      ...prev,
      installation_service_id: service.id,
      installation_service: service,
      trabajo: service.trabajo || '',
      descripcion_trabajo: service.descripcion || '', // Cargar la descripcion del catálogo
      cantidad_manual: cantidadInicial
    }));
  };

  // Calcular precio según tipo de instalación
  const calculateInstallationPrice = (): number => {
    // Si el usuario editó el precio (mano_obra_m2), usarlo directamente
    if (newInstallation.mano_obra_m2) {
      return newInstallation.mano_obra_m2;
    }
    
    // Si no hay precio editado, calcular desde el catálogo
    if (!newInstallation.installation_service || !newInstallation.tipo_instalacion) {
      return 0;
    }

    const service = newInstallation.installation_service;
    if (newInstallation.tipo_instalacion === 'standard') {
      return service.precio_standard || 0;
    } else if (newInstallation.tipo_instalacion === 'compleja') {
      return service.precio_complejo || 0;
    }
    
    return 0;
  };

  // Funciones para manejar fletes
  const handleAddFreight = () => {
    if (!newFreight.tipo_envio.trim()) return;
    
    setFormData(prev => ({
      ...prev,
      freights: [...prev.freights, { ...newFreight }]
    }));
    
    setNewFreight({
      tipo_envio: '',
      monto: 0,
      tipo: 'fijo',
      currency: 'ARS'
    });
    setFreightDialog(false);
  };

  const handleDeleteFreight = (index: number) => {
    setFormData(prev => ({
      ...prev,
      freights: prev.freights.filter((_, i) => i !== index)
    }));
  };

  // Calcular totales separados por moneda
  const calculateTotals = () => {
    let materialesUSD = 0;
    let materialesARS = 0; // Mantenemos esta variable por compatibilidad pero no se mostrará
    let totalInstalacion = 0;
    
    formData.environments.forEach(env => {
      env.materials.forEach(mat => {
        const subtotal = mat.unidades * mat.precio_unitario;
        if (mat.currency === 'USD') {
          materialesUSD += subtotal;
        }
        // Nota: Ignoramos los materiales en ARS para los totales de materiales
      });
      
      if (env.installations && env.installations.length > 0) {
        // Calcular área total del ambiente
        const areaTotal = env.materials.reduce((total, material) => {
          return total + (material.metros_cuadrados || 0);
        }, 0);
        
        // Sumar todas las instalaciones de este ambiente
        for (const installation of env.installations) {
          let subtotalInstalacion = 0;
          
          if (installation.installation_service && installation.tipo_instalacion) {
            // Usar el precio editado (mano_obra_m2) si existe, sino usar el del catálogo
            const precio = installation.mano_obra_m2
              ? installation.mano_obra_m2
              : (installation.tipo_instalacion === 'standard' 
                ? installation.installation_service.precio_standard 
                : installation.installation_service.precio_complejo);
            
            if (precio) {
              subtotalInstalacion = precio * (installation.cantidad_manual ?? 1);
            }
          } else if (installation.mano_obra_m2) {
            // Para instalaciones custom: si unidad es 'global', no multiplicar por cantidad
            const esGlobal = installation.unidad?.toLowerCase() === 'global';
            subtotalInstalacion = esGlobal ? installation.mano_obra_m2 : installation.mano_obra_m2 * (installation.cantidad_manual ?? 1);
          }
          
          totalInstalacion += subtotalInstalacion;
        }
      }
    });
    
    // Los fletes siempre son en ARS
    const totalFletes = formData.freights.reduce((sum, freight) => sum + freight.monto, 0);
    
    // Calcular totales con descuentos por ambiente
    const materialesConDescuento = formData.environments.reduce((total, env) => {
      const subtotalEnv = env.materials
        .filter(m => m.currency === 'USD')
        .reduce((sum, mat) => sum + (mat.unidades * mat.precio_unitario), 0);
      const descuento = env.descuento_materiales || 0;
      return total + (subtotalEnv * (1 - descuento / 100));
    }, 0);
    const instalacionConDescuento = totalInstalacion * (1 - formData.descuento_instalacion / 100);
    const fletesConDescuento = totalFletes * (1 - formData.descuento_fletes / 100);
    
    return {
      materialesUSD,
      materialesARS,
      materialesConDescuento,
      instalacion: totalInstalacion,
      instalacionConDescuento,
      fletes: totalFletes,
      fletesConDescuento,
      totalUSD: materialesUSD,
      totalARS: materialesARS + totalInstalacion + totalFletes
    };
  };

  const formatCurrency = (amount: number, type: 'material' | 'flete' | 'instalacion' = 'material') => {
    // Siempre mostrar símbolo antes del valor, miles con punto y decimales con coma
    if (type === 'flete' || type === 'instalacion') {
      return `AR$ ${amount.toLocaleString('es-AR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
    } else {
      return `U$ ${amount.toLocaleString('es-AR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
    }
  };

  // Formatear moneda específica para installation services
  const formatInstallationCurrency = (amount: number, currency: string) => {
    if (currency === 'USD') {
      return `U$ ${amount.toLocaleString('es-AR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
    } else {
      return `AR$ ${amount.toLocaleString('es-AR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
    }
  };

  // Manejar envío del formulario
  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    
    const newErrors: Record<string, string> = {};
    
    if (!formData.proyecto.trim()) {
      newErrors.proyecto = 'El nombre del proyecto es requerido';
    }
    
    if (!formData.cliente.trim()) {
      newErrors.cliente = 'El nombre del cliente es requerido';
    }
    
    if (!formData.vendedor.trim()) {
      newErrors.vendedor = 'El nombre del vendedor es requerido';
    }
    
    if (formData.email_cliente && !/\S+@\S+\.\S+/.test(formData.email_cliente)) {
      newErrors.email_cliente = 'El email no tiene un formato válido';
    }
    
    if (formData.email_vendedor && !/\S+@\S+\.\S+/.test(formData.email_vendedor)) {
      newErrors.email_vendedor = 'El email no tiene un formato válido';
    }
    
    setErrors(newErrors);
    
    if (Object.keys(newErrors).length > 0) {
      return;
    }
    
    setLoading(true);
    setError(null);
    
    try {
      
      // 1. Crear el presupuesto básico
      const budgetData: BudgetCreate = {
        proyecto: formData.proyecto,
        cliente: formData.cliente,
        responsable: formData.responsable,
        email_cliente: formData.email_cliente,
        tel_cliente: formData.tel_cliente
      };
      
      const newBudget = await BudgetApiService.createBudget(budgetData);
      
      // Si el título de la versión está vacío, usar el nombre del proyecto
      const versionTitle = formData.version_titulo.trim() ? 
        formData.version_titulo : 
        formData.proyecto;
      

      // 2. Crear la primera versión
      const versionData: BudgetVersionCreate = {
        titulo: versionTitle,
        vendedor: formData.vendedor,
        email_vendedor: formData.email_vendedor,
        tel_vendedor: formData.tel_vendedor,
        condiciones_contratacion: formData.condiciones_contratacion,
        condiciones_adicionales: formData.condiciones_adicionales,
        descuento_materiales: formData.descuento_materiales,
        descuento_instalacion: formData.descuento_instalacion,
        descuento_fletes: formData.descuento_fletes,
        nombre: versionTitle,
        condiciones: formData.condiciones_contratacion
      };


      const newVersion = await BudgetApiService.createBudgetVersion(newBudget.id, versionData);
      
      // 3. Crear ambientes y sus materiales
      for (let i = 0; i < formData.environments.length; i++) {
        const environment = formData.environments[i];
        
        const envData: EnvironmentCreate = {
          nombre: environment.nombre,
          descripcion: environment.descripcion,
          orden: environment.orden,
          descuento_materiales: environment.descuento_materiales || 0
        };
        
        const newEnv = await BudgetApiService.createEnvironment(newBudget.id, newVersion.id, envData);
        
        // Crear materiales para este ambiente
        for (let j = 0; j < environment.materials.length; j++) {
          const material = environment.materials[j];
          
          if (material.es_in_situ) {
            // Material in situ (nuevo)
            const matData: EnvironmentMaterialCreate = {
              descripcion: material.descripcion,
              codigo: material.codigo,
              unidades: material.unidades,
              metros2: material.metros_cuadrados || undefined, // ✅ Solo incluir si tiene valor (materiales de estructura no tienen m2)
              precio_unitario: material.precio_unitario,
              monto: material.unidades * material.precio_unitario,
              currency: material.currency,
              es_in_situ: true,
              con_desperdicio: material.con_desperdicio || false,
              con_estructura: material.con_estructura || false
            };
            
            const newMat = await BudgetApiService.createEnvironmentMaterial(newEnv.id, matData);
          } else if (material.product_id) {
            // Producto existente
            const matData: EnvironmentMaterialCreate = {
              product_id: material.product_id,
              descripcion: material.descripcion,
              codigo: material.codigo,
              unidades: material.unidades,
              metros2: material.metros_cuadrados || undefined, // ✅ Solo incluir si tiene valor (materiales de estructura no tienen m2)
              precio_unitario: material.precio_unitario,
              monto: material.unidades * material.precio_unitario,
              currency: material.currency,
              es_in_situ: false,
              con_desperdicio: material.con_desperdicio || false,
              con_estructura: material.con_estructura || false,
              observaciones: material.observaciones // ✅ Incluir observaciones para detectar estructura
            };
            
            const newMat = await BudgetApiService.createEnvironmentMaterialFromProduct(newEnv.id, material.product_id, matData);
          }
        }
        
        // Crear instalaciones si existen (pueden ser múltiples)
        if (environment.installations && environment.installations.length > 0) {
          
          // Calcular área total del ambiente basado en los materiales
          const areaTotal = environment.materials.reduce((total, material) => {
            return total + (material.metros_cuadrados || 0);
          }, 0);
          
          // Crear cada instalación
          for (let inst = 0; inst < environment.installations.length; inst++) {
            const installation = environment.installations[inst];
            
            // Calcular subtotal de instalación según el nuevo esquema
            let subtotalCalculado = 0;
            let mano_obra_m2_final = installation.mano_obra_m2;
            
            if (installation.installation_service && installation.tipo_instalacion) {
              // Si no hay precio editado, usar el del catálogo
              if (!installation.mano_obra_m2 || installation.mano_obra_m2 === 0) {
                mano_obra_m2_final = installation.tipo_instalacion === 'standard' 
                  ? installation.installation_service.precio_standard 
                  : installation.installation_service.precio_complejo;
              }
              
              if (mano_obra_m2_final) {
                subtotalCalculado = mano_obra_m2_final * (installation.cantidad_manual ?? 1);
              }
            } else if (installation.mano_obra_m2) {
              // Para instalaciones custom: si unidad es 'global', no multiplicar por cantidad
              const esGlobal = installation.unidad?.toLowerCase() === 'global';
              subtotalCalculado = esGlobal ? installation.mano_obra_m2 : installation.mano_obra_m2 * (installation.cantidad_manual ?? 1);
            }
            
            const instData: EnvironmentInstallationCreate = {
              installation_service_id: installation.installation_service_id,
              tipo_instalacion: installation.tipo_instalacion,
              cantidad_manual: installation.cantidad_manual,
              trabajo: installation.trabajo,
              descripcion_trabajo: installation.descripcion_trabajo,
              mano_obra_m2: mano_obra_m2_final,
              subtotal: subtotalCalculado,
              currency: installation.currency,
              unidad: installation.unidad
            };
            
            
            const newInst = await BudgetApiService.createEnvironmentInstallation(newEnv.id, instData);
          }
        }
      }
      
      // 4. Crear fletes
      for (let k = 0; k < formData.freights.length; k++) {
        const freight = formData.freights[k];
        
        const freightData: FreightCreate = {
          tipo_envio: freight.tipo_envio,
          descripcion_detalle: freight.descripcion_detalle,
          monto: freight.monto,
          tipo: freight.tipo,
          currency: freight.currency
        };
        
        const newFreight = await BudgetApiService.createFreight(newBudget.id, newVersion.id, freightData);
      }
      
  // Forzar recálculo de totales de la versión para que total_instalacion se guarde correctamente
  await BudgetApiService.calculateVersionTotals(newBudget.id, newVersion.id);
  navigate(`/budgets/${newBudget.id}`);
      
    } catch (err: any) {
      console.error('❌ Error creating budget:', err);
      console.error('Error details:', err.response?.data);
      setError(err.response?.data?.detail || err.message || 'Error al crear el presupuesto');
    } finally {
      setLoading(false);
    }
  };

  // Efecto para manejar la alerta de stock
  useEffect(() => {
    if (alertStock) {
      setShowAlertStock(true);
      const timer = setTimeout(() => {
        setShowAlertStock(false);
        setAlertStock(null);
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [alertStock]);

  const totals = calculateTotals();

  return (
    <Box p={3}>
      <Box mb={3}>
        <Button
          startIcon={<ArrowBack />}
          onClick={() => navigate('/budgets')}
          sx={{ mb: 2 }}
        >
          Volver a Presupuestos
        </Button>
        
        {/* Título editable centrado */}
        <Box textAlign="center" mb={3}>
          <TextField
            fullWidth
            variant="standard"
            InputProps={{ 
              style: { fontSize: '2rem', fontWeight: 'bold', textAlign: 'center' },
              disableUnderline: true
            }}
            placeholder={formData.proyecto ? `${formData.proyecto} (título por defecto)` : "Título del Presupuesto"}
            value={formData.version_titulo}
            onChange={handleChange('version_titulo')}
            error={!!errors.version_titulo}
          />
        </Box>
        
        {/* Información no editable */}
        <Box display="flex" justifyContent="center" gap={4} mb={3}>
          <Typography variant="subtitle1" color="text.secondary">
            <strong>ID:</strong> Se asignará al guardar
          </Typography>
          <Typography variant="subtitle1" color="text.secondary">
            <strong>Versión:</strong> 1.0
          </Typography>
          <Typography variant="subtitle1" color="text.secondary">
            <strong>Fecha:</strong> {new Date().toLocaleDateString()}
          </Typography>
        </Box>
      </Box>

      <form onSubmit={handleSubmit}>
        <Stack spacing={3}>
          {/* Nueva Tarjeta de Información Combinada */}
          <Card>
            <CardHeader 
              title="Información del Presupuesto"
              titleTypographyProps={{ variant: 'h6' }}
              avatar={<Business color="primary" />}
            />
            <CardContent>
              <Box display="flex" flexWrap="wrap" gap={3}>
                {/* Columna izquierda - Información del Cliente */}
                <Box flex="1" minWidth="300px">
                  <Typography variant="subtitle1" fontWeight="medium" gutterBottom sx={{ color: 'primary.main' }}>
                    Datos del Cliente
                  </Typography>
                  <Stack spacing={2}>
                    <TextField
                      fullWidth
                      label="Nombre del Proyecto"
                      value={formData.proyecto}
                      onChange={handleChange('proyecto')}
                      error={!!errors.proyecto}
                      helperText={errors.proyecto || "Nombre del proyecto o identificador único"}
                      required
                    />
                    
                    <TextField
                      fullWidth
                      label="Nombre del Cliente"
                      value={formData.cliente}
                      onChange={handleChange('cliente')}
                      error={!!errors.cliente}
                      helperText={errors.cliente}
                      required
                    />
                    
                    <TextField
                      fullWidth
                      label="Responsable del Proyecto"
                      value={formData.responsable}
                      onChange={handleChange('responsable')}
                      helperText="Persona responsable del proyecto (opcional)"
                    />
                    
                    <TextField
                      fullWidth
                      label="Email del Cliente"
                      type="email"
                      value={formData.email_cliente}
                      onChange={handleChange('email_cliente')}
                      error={!!errors.email_cliente}
                      helperText={errors.email_cliente}
                    />
                    
                    <TextField
                      fullWidth
                      label="Teléfono del Cliente"
                      value={formData.tel_cliente}
                      onChange={handleChange('tel_cliente')}
                    />

                    {/* Oculto pero movido aquí para mantener consistencia con el título */}
                    <TextField
                      fullWidth
                      label="Título de la Versión"
                      value={formData.version_titulo}
                      onChange={handleChange('version_titulo')}
                      error={!!errors.version_titulo}
                      helperText={errors.version_titulo}
                      sx={{ display: 'none' }}
                    />
                  </Stack>
                </Box>
                
                {/* Columna derecha - Información del Vendedor */}
                <Box flex="1" minWidth="300px">
                  <Typography variant="subtitle1" fontWeight="medium" gutterBottom sx={{ color: 'primary.main' }}>
                    Datos del Vendedor
                  </Typography>
                  <Stack spacing={2}>
                    <TextField
                      fullWidth
                      label="Nombre del Vendedor"
                      value={formData.vendedor}
                      onChange={handleChange('vendedor')}
                      error={!!errors.vendedor}
                      helperText={errors.vendedor}
                      required
                    />
                    
                    <TextField
                      fullWidth
                      label="Email del Vendedor"
                      type="email"
                      value={formData.email_vendedor}
                      onChange={handleChange('email_vendedor')}
                      error={!!errors.email_vendedor}
                      helperText={errors.email_vendedor}
                    />
                    
                    <TextField
                      fullWidth
                      label="Teléfono del Vendedor"
                      value={formData.tel_vendedor}
                      onChange={handleChange('tel_vendedor')}
                    />
                  </Stack>
                </Box>
              </Box>
            </CardContent>
          </Card>

          {/* 3. Tarjeta de Ambientes */}
          <Card>
            <CardHeader 
              title="Ambientes y Materiales"
              titleTypographyProps={{ variant: 'h6' }}
              avatar={<Home color="primary" />}
              subheader={`${formData.environments.length} ambiente(s) configurado(s)`}
              action={
                <Button
                  variant="contained"
                  size="small"
                  startIcon={<Add />}
                  onClick={() => setEnvironmentDialog(true)}
                >
                  Agregar Ambiente
                </Button>
              }
            />
            <CardContent>
              {formData.environments.length > 0 ? (
                <Box>
                  <TableContainer component={Paper} sx={{ mb: 3 }}>
                    <Table>
                      <TableHead>
                        <TableRow sx={{ backgroundColor: 'primary.main' }}>
                          <TableCell colSpan={2} sx={{ color: 'white', fontWeight: 'bold' }}>Ambientes y Materiales</TableCell>
                          <TableCell align="right" sx={{ color: 'white' }}>Precio Unitario</TableCell>
                          <TableCell align="right" sx={{ color: 'white' }}>Unidades</TableCell>
                          <TableCell align="right" sx={{ color: 'white' }}>Total</TableCell>
                          <TableCell align="center" sx={{ color: 'white' }}>Acciones</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {formData.environments
                          .filter(env => env.nombre !== GENERAL_INSTALLATIONS_ENV_NAME)
                          .map((env, envIndex) => {
                            // Obtener el índice real en formData.environments
                            const actualEnvIndex = formData.environments.findIndex(e => e === env);
                            return (
                          <React.Fragment key={actualEnvIndex}>
                            {/* Fila del ambiente */}
                            <TableRow sx={{ backgroundColor: 'rgba(76, 175, 80, 0.08)' }}>
                              <TableCell colSpan={4}>
                                {editingEnvironmentIndex === actualEnvIndex ? (
                                  // Modo edición: TextField con botones
                                  <Box display="flex" alignItems="center" gap={1}>
                                    <TextField
                                      size="small"
                                      value={editingEnvironmentName}
                                      onChange={(e) => setEditingEnvironmentName(e.target.value)}
                                      autoFocus
                                      sx={{ minWidth: 200 }}
                                    />
                                    <IconButton
                                      size="small"
                                      color="primary"
                                      onClick={() => handleSaveEnvironmentName(actualEnvIndex)}
                                      disabled={!editingEnvironmentName.trim()}
                                    >
                                      <CheckCircle />
                                    </IconButton>
                                    <IconButton
                                      size="small"
                                      color="error"
                                      onClick={handleCancelEditEnvironmentName}
                                    >
                                      <Cancel />
                                    </IconButton>
                                  </Box>
                                ) : (
                                  // Modo visualización: Nombre con ícono de editar
                                  <Box display="flex" alignItems="center" gap={1}>
                                    <Typography variant="subtitle1" fontWeight="medium" sx={{ color: '#2e7d32' }}>
                                      {env.nombre}
                                    </Typography>
                                    <IconButton
                                      size="small"
                                      onClick={() => handleStartEditEnvironmentName(actualEnvIndex, env.nombre)}
                                    >
                                      <Edit fontSize="small" />
                                    </IconButton>
                                    {env.descripcion && (
                                      <Chip 
                                        label="Ver descripción" 
                                        size="small" 
                                        variant="outlined"
                                        onClick={() => alert(env.descripcion)}
                                      />
                                    )}
                                  </Box>
                                )}
                              </TableCell>
                              <TableCell colSpan={2} align="right">
                                <Box display="flex" justifyContent="flex-end" gap={1}>
                                  <Button
                                    size="small"
                                    startIcon={<Add />}
                                    variant="outlined"
                                    onClick={() => {
                                      setSelectedEnvironmentIndex(actualEnvIndex);
                                      setMaterialDialog(true);
                                    }}
                                  >
                                    Material
                                  </Button>
                                  <IconButton
                                    size="small"
                                    onClick={() => handleDeleteEnvironment(actualEnvIndex)}
                                    color="error"
                                  >
                                    <Delete />
                                  </IconButton>
                                </Box>
                              </TableCell>
                            </TableRow>
                            
                            {/* Filas de materiales */}
                            {env.materials.length > 0 ? (
                              env.materials.map((material, matIndex) => (
                                <TableRow key={`material-${actualEnvIndex}-${matIndex}`}>
                                  <TableCell width="5%">
                                    {material.es_in_situ ? (
                                      <Chip label="Manual" size="small" color="warning" />
                                    ) : material.product?.descuento === -1 ? (
                                      <Chip label="Outlet" size="small" color="warning" />
                                    ) : (
                                      <Chip label="Catálogo" size="small" color="info" />
                                    )}
                                  </TableCell>
                                  <TableCell>
                                    <Typography variant="body2" fontWeight="medium">
                                      {material.codigo} - {material.descripcion}
                                    </Typography>
                                    {material.observaciones?.includes('Material de estructura') ? (
                                      <Chip 
                                        label="Estructura" 
                                        size="small" 
                                        color="secondary" 
                                        variant="outlined"
                                      />
                                    ) : null}
                                    {material.observaciones && !material.observaciones.includes('Material de estructura') && (
                                      <Typography variant="caption" color="text.secondary" display="block">
                                        {material.observaciones}
                                      </Typography>
                                    )}
                                    {material.product && (
                                      <Typography variant="caption" color="text.secondary" display="block">
                                        SKU: {material.product.sku || 'Sin SKU'} 
                                        {material.metros_cuadrados ? ` | ${material.metros_cuadrados} m²` : ''}
                                      </Typography>
                                    )}
                                  </TableCell>
                                  <TableCell align="right">
                                    {formatCurrency(material.precio_unitario, material.currency === 'ARS' ? 'instalacion' : 'material')}
                                  </TableCell>
                                  <TableCell align="right">
                                    <TextField
                                      type="number"
                                      size="small"
                                      value={material.unidades}
                                      onChange={(e) => {
                                        const newValue = parseInt(e.target.value) || 1;
                                        if (newValue < 1) return; // Validación mínima
                                        
                                        setFormData(prev => {
                                          const newEnvironments = [...prev.environments];
                                          newEnvironments[actualEnvIndex].materials[matIndex].unidades = newValue;
                                          return { ...prev, environments: newEnvironments };
                                        });
                                      }}
                                      sx={{ width: '90px' }}
                                      inputProps={{ 
                                        min: 1, 
                                        style: { textAlign: 'right', padding: '8px' } 
                                      }}
                                    />
                                  </TableCell>
                                  <TableCell align="right">
                                    {formatCurrency(material.unidades * material.precio_unitario, material.currency === 'ARS' ? 'instalacion' : 'material')}
                                  </TableCell>
                                  <TableCell align="center">
                                    <IconButton
                                      size="small"
                                      onClick={() => handleDeleteMaterial(actualEnvIndex, matIndex)}
                                      color="error"
                                    >
                                      <Delete fontSize="small" />
                                    </IconButton>
                                  </TableCell>
                                </TableRow>
                              ))
                            ) : (
                              <TableRow>
                                <TableCell colSpan={6} align="center">
                                  <Typography variant="body2" color="text.secondary">
                                    No hay materiales agregados
                                  </Typography>
                                </TableCell>
                              </TableRow>
                            )}
                            
                            {/* Fila de subtotal para este ambiente */}
                            <TableRow sx={{ backgroundColor: 'rgba(76, 175, 80, 0.04)' }}>
                              <TableCell colSpan={3} align="right" sx={{ fontWeight: 'bold' }}>
                                Subtotal Ambiente:
                              </TableCell>
                              <TableCell align="right" sx={{ fontWeight: 'bold' }}>
                                {env.materials.reduce((total, mat) => total + mat.unidades, 0)}
                              </TableCell>
                              <TableCell align="right" sx={{ fontWeight: 'bold' }}>
                                {formatCurrency(env.materials
                                  .filter(m => m.currency === 'USD')
                                  .reduce((total, mat) => total + (mat.unidades * mat.precio_unitario), 0), 'material')}
                              </TableCell>
                              <TableCell></TableCell>
                            </TableRow>
                            {/* Fila de descuento por ambiente */}
                            <TableRow>
                              <TableCell colSpan={3} align="right">
                                <Box display="flex" justifyContent="flex-end" alignItems="center" gap={2}>
                                  <Typography variant="body2">Descuento Ambiente (%)</Typography>
                                  <TextField
                                    type="number"
                                    size="small"
                                    value={Math.round(env.descuento_materiales || 0)}
                                    onChange={(e) => {
                                      const newEnvironments = [...formData.environments];
                                      newEnvironments[envIndex].descuento_materiales = parseFloat(e.target.value) || 0;
                                      setFormData(prev => ({ ...prev, environments: newEnvironments }));
                                    }}
                                    inputProps={{ min: 0, max: 100, step: 5, style: { width: '60px', textAlign: 'right' } }}
                                  />
                                </Box>
                              </TableCell>
                              <TableCell></TableCell>
                              <TableCell align="right" sx={{ color: 'success.main' }}>
                                {(env.descuento_materiales || 0) > 0 ? `- ${formatCurrency(
                                  env.materials
                                    .filter(m => m.currency === 'USD')
                                    .reduce((total, mat) => total + (mat.unidades * mat.precio_unitario), 0) * ((env.descuento_materiales || 0) / 100),
                                  'material'
                                )}` : ''}
                              </TableCell>
                              <TableCell></TableCell>
                            </TableRow>
                            {/* Fila de total ambiente con descuento */}
                            {(env.descuento_materiales || 0) > 0 && (
                              <TableRow sx={{ backgroundColor: 'rgba(76, 175, 80, 0.08)' }}>
                                <TableCell colSpan={3} align="right" sx={{ fontWeight: 'bold' }}>
                                  Total Ambiente con Descuento:
                                </TableCell>
                                <TableCell></TableCell>
                                <TableCell align="right" sx={{ fontWeight: 'bold', color: 'success.dark' }}>
                                  {formatCurrency(
                                    env.materials
                                      .filter(m => m.currency === 'USD')
                                      .reduce((total, mat) => total + (mat.unidades * mat.precio_unitario), 0) * (1 - (env.descuento_materiales || 0) / 100),
                                    'material'
                                  )}
                                </TableCell>
                                <TableCell></TableCell>
                              </TableRow>
                            )}
                            {/* Línea divisoria después del subtotal */}
                            {envIndex < formData.environments.length - 1 && (
                              <TableRow>
                                <TableCell colSpan={6} sx={{ padding: 0 }}>
                                  <Divider sx={{ borderBottomWidth: 2, borderColor: '#e0e0e0' }} />
                                </TableCell>
                              </TableRow>
                            )}
                          </React.Fragment>
                        );
                      })}
                        
                        {/* Fila de total general de todos los ambientes */}
                        <TableRow sx={{ backgroundColor: 'primary.main' }}>
                          <TableCell colSpan={3} align="right" sx={{ color: 'white', fontWeight: 'bold' }}>
                            TOTAL MATERIALES:
                          </TableCell>
                          <TableCell align="right" sx={{ color: 'white', fontWeight: 'bold' }}>
                            {formData.environments.reduce((total, env) => 
                              total + env.materials.reduce((envTotal, mat) => envTotal + mat.unidades, 0), 0)}
                          </TableCell>
                          <TableCell colSpan={2} align="right" sx={{ color: 'white', fontWeight: 'bold' }}>
                            {formatCurrency(
                              formData.environments.reduce((total, env) => 
                                total + env.materials
                                  .filter(m => m.currency === 'USD')
                                  .reduce((matTotal, mat) => matTotal + (mat.unidades * mat.precio_unitario), 0), 0),
                              'material'
                            )}
                          </TableCell>
                        </TableRow>
                        
                        {/* Fila de total con descuentos por ambiente */}
                        <TableRow sx={{ backgroundColor: 'rgba(76, 175, 80, 0.12)' }}>
                          <TableCell colSpan={3} align="right" sx={{ fontWeight: 'bold' }}>
                            TOTAL CON DESCUENTOS:
                          </TableCell>
                          <TableCell colSpan={3} align="right" sx={{ fontWeight: 'bold', color: 'success.dark' }}>
                            {formatCurrency(totals.materialesConDescuento, 'material')}
                          </TableCell>
                        </TableRow>
                        </TableBody>
                    </Table>
                  </TableContainer>

                  {/* Control de descuento global */}
                  {formData.environments.filter(e => e.nombre !== GENERAL_INSTALLATIONS_ENV_NAME).length > 0 && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, bgcolor: 'grey.100', px: 3, py: 1.5, borderRadius: 1, mt: 2, justifyContent: 'center' }}>
                      <Typography variant="body1" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>
                        Descuento global:
                      </Typography>
                      <TextField
                        size="small"
                        type="number"
                        value={globalDiscountInput}
                        onChange={(e) => setGlobalDiscountInput(e.target.value)}
                        placeholder={commonDiscount === null ? 'Mixto' : ''}
                        inputProps={{ min: 0, max: 100, step: 5, style: { width: '60px', textAlign: 'center' } }}
                        sx={{ 
                          width: '90px',
                          '& input::placeholder': { 
                            color: 'warning.main', 
                            opacity: 1,
                            fontStyle: 'italic'
                          }
                        }}
                      />
                      <Typography variant="body1" color="text.secondary">%</Typography>
                      <Button
                        size="small"
                        variant="contained"
                        onClick={applyGlobalDiscount}
                        disabled={globalDiscountInput === ''}
                      >
                        Aplicar a todos los ambientes
                      </Button>
                    </Box>
                  )}
                </Box>
              ) : (
                <Alert severity="info">
                  No hay ambientes configurados. Agregue el primer ambiente para comenzar.
                </Alert>
              )}
            </CardContent>
          </Card>

          {/* Nueva Tarjeta de Instalación */}
          <Card>
            <CardHeader 
              title="Servicios de Instalación"
              titleTypographyProps={{ variant: 'h6' }}
              avatar={<Build color="primary" />}
              subheader="Servicios de instalación para los ambientes"
              action={
                <Button
                  variant="contained"
                  size="small"
                  startIcon={<Add />}
                  disabled={formData.environments.length === 0}
                  onClick={() => {
                    if (formData.environments.length > 0) {
                      // Abrir diálogo sin preseleccionar ningún ambiente
                      setSelectedEnvironmentIndex(null);
                      setInstallationDialog(true);
                    }
                  }}
                >
                  Agregar Instalación
                </Button>
              }
            />
            <CardContent>
              {formData.environments.some(env => env.installations && env.installations.length > 0) ? (
                <Box>
                  <TableContainer component={Paper}>
                    <Table>
                      <TableHead>
                        <TableRow sx={{ backgroundColor: 'primary.main' }}>
                          <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Ambiente</TableCell>
                          <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Trabajo</TableCell>
                          <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Descripción</TableCell>
                          <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Unidad</TableCell>
                          <TableCell align="right" sx={{ color: 'white', fontWeight: 'bold' }}>Precio por Unidad</TableCell>
                          <TableCell align="right" sx={{ color: 'white', fontWeight: 'bold' }}>Cantidad de Unidades</TableCell>
                          <TableCell align="right" sx={{ color: 'white', fontWeight: 'bold' }}>Total</TableCell>
                          <TableCell align="center" sx={{ color: 'white', fontWeight: 'bold' }}>Acciones</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {formData.environments
                          .filter(env => env.installations && env.installations.length > 0)
                          .map((env, envIndex) => {
                            // Calcular área total del ambiente
                            const areaTotal = env.materials.reduce((total, material) => {
                              return total + (material.metros_cuadrados || 0);
                            }, 0);
                            
                            // Renderizar una fila por cada instalación en el ambiente
                            return env.installations.map((installation, instIndex) => {
                              // Calcular subtotal de instalación según el nuevo esquema
                              let subtotal = 0;
                              if (installation.installation_service && installation.tipo_instalacion) {
                                // Usar el precio editado (mano_obra_m2) si existe, sino usar el precio del catálogo
                                const precio = installation.mano_obra_m2
                                  ? installation.mano_obra_m2
                                  : (installation.tipo_instalacion === 'standard' 
                                    ? installation.installation_service.precio_standard 
                                    : installation.installation_service.precio_complejo);
                                
                                if (precio) {
                                  subtotal = precio * (installation.cantidad_manual || 1);
                                }
                              } else if (installation.mano_obra_m2) {
                                // Para instalaciones custom: si unidad es 'global', no multiplicar por cantidad
                                const esGlobal = installation.unidad?.toLowerCase() === 'global';
                                subtotal = esGlobal ? installation.mano_obra_m2 : installation.mano_obra_m2 * (installation.cantidad_manual || 1);
                              }
                              
                              return (
                                <TableRow key={`installation-${envIndex}-${instIndex}`}>
                                  <TableCell>{env.nombre}</TableCell>
                                  <TableCell>
                                    {installation?.installation_service?.trabajo || installation?.trabajo || 'Instalación personalizada'}
                                  </TableCell>
                                  <TableCell>
                                    {installation?.installation_service?.descripcion || installation?.descripcion_trabajo || '-'}
                                  </TableCell>
                                  <TableCell>
                                    {installation?.unidad || installation?.installation_service?.unidad || ''}
                                  </TableCell>
                                  <TableCell align="right">
                                    {installation?.installation_service && installation?.tipo_instalacion ? (
                                      installation.mano_obra_m2 
                                        ? formatInstallationCurrency(installation.mano_obra_m2, 'ARS')
                                        : (installation.tipo_instalacion === 'standard' 
                                          ? formatInstallationCurrency(installation.installation_service.precio_standard || 0, installation.installation_service.moneda_standard)
                                          : formatInstallationCurrency(installation.installation_service.precio_complejo || 0, installation.installation_service.moneda_compleja))
                                    ) : (
                                      // Para instalaciones custom con unidad global, mostrar "-"
                                      installation?.unidad?.toLowerCase() === 'global' 
                                        ? '-'
                                        : formatCurrency(installation?.mano_obra_m2 || 0, 'instalacion')
                                    )}
                                  </TableCell>
                                  <TableCell align="right">
                                    {installation?.cantidad_manual || areaTotal.toFixed(2)}
                                  </TableCell>
                                  <TableCell align="right">
                                    {installation?.installation_service && installation?.tipo_instalacion ? (
                                      installation.tipo_instalacion === 'standard' 
                                        ? formatInstallationCurrency(subtotal, installation.installation_service.moneda_standard)
                                        : formatInstallationCurrency(subtotal, installation.installation_service.moneda_compleja)
                                    ) : (
                                      formatCurrency(subtotal, 'instalacion')
                                    )}
                                  </TableCell>
                                  <TableCell align="center">
                                    <IconButton
                                      size="small"
                                      onClick={() => {
                                        setFormData(prev => {
                                          const newEnvironments = [...prev.environments];
                                          newEnvironments[envIndex].installations = newEnvironments[envIndex].installations.filter((_, idx) => idx !== instIndex);
                                          return { ...prev, environments: newEnvironments };
                                        });
                                      }}
                                      color="error"
                                    >
                                      <Delete fontSize="small" />
                                    </IconButton>
                                  </TableCell>
                                </TableRow>
                              );
                            });
                        })}
                        
                        {/* Fila de total de instalación */}
                        <TableRow sx={{ backgroundColor: 'primary.main' }}>
                          <TableCell colSpan={6} align="right" sx={{ color: 'white', fontWeight: 'bold' }}>
                            TOTAL INSTALACIÓN:
                          </TableCell>
                          <TableCell colSpan={2} align="right" sx={{ color: 'white', fontWeight: 'bold' }}>
                            {(() => {
                              const totales = { ARS: 0, USD: 0, other: 0 };
                              
                              formData.environments.forEach(env => {
                                if (!env.installations || env.installations.length === 0) return;
                                
                                const areaTotal = env.materials.reduce(
                                  (sum, mat) => sum + (mat.metros_cuadrados || 0), 0
                                );
                                
                                // Sumar todas las instalaciones del ambiente
                                env.installations.forEach(installation => {
                                  let subtotal = 0;
                                  let moneda = 'ARS';
                                  
                                  if (installation.installation_service && installation.tipo_instalacion) {
                                    // Usar el precio editado (mano_obra_m2) si existe, sino usar el del catálogo
                                    const precio = installation.mano_obra_m2
                                      ? installation.mano_obra_m2
                                      : (installation.tipo_instalacion === 'standard' 
                                        ? installation.installation_service.precio_standard 
                                        : installation.installation_service.precio_complejo);
                                    
                                    moneda = installation.tipo_instalacion === 'standard'
                                      ? installation.installation_service.moneda_standard
                                      : installation.installation_service.moneda_compleja;
                                    
                                    if (precio) {
                                      subtotal = precio * (installation.cantidad_manual ?? 1);
                                    }
                                  } else if (installation.mano_obra_m2) {
                                    // Para instalaciones custom: si unidad es 'global', no multiplicar por cantidad
                                    const esGlobal = installation.unidad?.toLowerCase() === 'global';
                                    subtotal = esGlobal ? installation.mano_obra_m2 : installation.mano_obra_m2 * (installation.cantidad_manual ?? 1);
                                    moneda = installation.currency || 'ARS';
                                  }
                                  
                                  if (moneda === 'ARS') {
                                    totales.ARS += subtotal;
                                  } else if (moneda === 'USD') {
                                    totales.USD += subtotal;
                                  } else {
                                    totales.other += subtotal;
                                  }
                                });
                              });
                              
                              const parts = [];
                              if (totales.ARS > 0) parts.push(formatInstallationCurrency(totales.ARS, 'ARS'));
                              if (totales.USD > 0) parts.push(formatInstallationCurrency(totales.USD, 'USD'));
                              if (totales.other > 0) parts.push(`${totales.other.toFixed(2)}`);
                              
                              return parts.join(' + ') || formatInstallationCurrency(0, 'ARS');
                            })()}
                          </TableCell>
                        </TableRow>
                        
                        {/* Fila de descuento de instalación */}
                        <TableRow>
                          <TableCell colSpan={4} align="right">
                            <Box display="flex" justifyContent="flex-end" alignItems="center" gap={2}>
                              <Typography variant="body2">Descuento (%)</Typography>
                              <TextField
                                type="number"
                                size="small"
                                value={Math.round(formData.descuento_instalacion)}
                                onChange={handleChange('descuento_instalacion')}
                                inputProps={{ min: 0, max: 100, step: 5, style: { width: '60px', textAlign: 'right' } }}
                              />
                            </Box>
                          </TableCell>
                          <TableCell colSpan={2} align="right" sx={{ fontWeight: 'bold', color: 'success.main' }}>
                            {formData.descuento_instalacion > 0 ? `- ${formatCurrency(
                              totals.instalacion * (formData.descuento_instalacion / 100),
                              'instalacion'
                            )}` : '0.00'}
                          </TableCell>
                        </TableRow>
                        
                        {/* Fila de total con descuento */}
                        <TableRow sx={{ backgroundColor: 'rgba(76, 175, 80, 0.12)' }}>
                          <TableCell colSpan={4} align="right" sx={{ fontWeight: 'bold' }}>
                            TOTAL FINAL CON DESCUENTO:
                          </TableCell>
                          <TableCell colSpan={2} align="right" sx={{ fontWeight: 'bold', color: 'success.dark' }}>
                            {formatCurrency(totals.instalacionConDescuento, 'instalacion')}
                          </TableCell>
                        </TableRow>
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Box>
              ) : (
                <Alert severity="info">
                  No hay servicios de instalación configurados. Agregue instalación a los ambientes según sea necesario.
                </Alert>
              )}
            </CardContent>
          </Card>

          {/* 4. Tarjeta de Fletes */}
          <Card>
            <CardHeader 
              title="Fletes y Envíos"
              titleTypographyProps={{ variant: 'h6' }}
              avatar={<LocalShipping color="primary" />}
              subheader={`${formData.freights.length} flete(s) configurado(s)`}
              action={
                <Button
                  variant="contained"
                  size="small"
                  startIcon={<Add />}
                  onClick={() => setFreightDialog(true)}
                >
                  Agregar Flete
                </Button>
              }
            />
            <CardContent>
              {formData.freights.length > 0 ? (
                <Box>
                  <TableContainer component={Paper}>
                    <Table>
                      <TableHead>
                        <TableRow sx={{ backgroundColor: 'primary.main' }}>
                          <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Tipo</TableCell>
                          <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Descripción</TableCell>
                          <TableCell align="right" sx={{ color: 'white', fontWeight: 'bold' }}>Monto</TableCell>
                          <TableCell align="center" sx={{ color: 'white', fontWeight: 'bold' }}>Acciones</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {formData.freights.map((freight, index) => (
                          <TableRow key={`freight-${index}`}>
                            <TableCell>
                              <Typography variant="body2" fontWeight="medium">
                                {freight.tipo_envio}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <TextField
                                size="small"
                                value={freight.descripcion_detalle || ''}
                                onChange={(e) => {
                                  const newFreights = [...formData.freights];
                                  newFreights[index] = { ...newFreights[index], descripcion_detalle: e.target.value };
                                  setFormData(prev => ({ ...prev, freights: newFreights }));
                                }}
                                placeholder="Detalle del flete"
                                inputProps={{ maxLength: 150 }}
                                sx={{ minWidth: 200 }}
                              />
                            </TableCell>
                            <TableCell align="right">
                              {formatCurrency(freight.monto, 'flete')}
                            </TableCell>
                            <TableCell align="center">
                              <IconButton
                                size="small"
                                onClick={() => handleDeleteFreight(index)}
                                color="error"
                              >
                                <Delete fontSize="small" />
                              </IconButton>
                            </TableCell>
                          </TableRow>
                        ))}
                        
                        {/* Fila de total de fletes */}
                        <TableRow sx={{ backgroundColor: 'primary.main' }}>
                          <TableCell colSpan={2} align="right" sx={{ color: 'white', fontWeight: 'bold' }}>
                            TOTAL FLETES:
                          </TableCell>
                          <TableCell align="right" sx={{ color: 'white', fontWeight: 'bold' }}>
                            {formatCurrency(
                              formData.freights.reduce((total, freight) => total + freight.monto, 0),
                              'flete'
                            )}
                          </TableCell>
                          <TableCell></TableCell>
                        </TableRow>
                        
                        {/* Fila de descuento de fletes */}
                        <TableRow>
                          <TableCell colSpan={2} align="right">
                            <Box display="flex" justifyContent="flex-end" alignItems="center" gap={2}>
                              <Typography variant="body2">Descuento (%)</Typography>
                              <TextField
                                type="number"
                                size="small"
                                value={Math.round(formData.descuento_fletes)}
                                onChange={handleChange('descuento_fletes')}
                                inputProps={{ min: 0, max: 100, step: 5, style: { width: '60px', textAlign: 'right' } }}
                              />
                            </Box>
                          </TableCell>
                          <TableCell align="right" sx={{ fontWeight: 'bold', color: 'success.main' }}>
                            {formData.descuento_fletes > 0 ? `- ${formatCurrency(
                              totals.fletes * (formData.descuento_fletes / 100),
                              'flete'
                            )}` : '0.00'}
                          </TableCell>
                          <TableCell></TableCell>
                        </TableRow>
                        
                        {/* Fila de total con descuento */}
                        <TableRow sx={{ backgroundColor: 'rgba(76, 175, 80, 0.12)' }}>
                          <TableCell colSpan={2} align="right" sx={{ fontWeight: 'bold' }}>
                            TOTAL FINAL CON DESCUENTO:
                          </TableCell>
                          <TableCell align="right" sx={{ fontWeight: 'bold', color: 'success.dark' }}>
                            {formatCurrency(totals.fletesConDescuento, 'flete')}
                          </TableCell>
                          <TableCell></TableCell>
                        </TableRow>
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Box>
              ) : (
                <Alert severity="info">
                  No hay fletes configurados. Agregue costos de envío según sea necesario.
                </Alert>
              )}
            </CardContent>
          </Card>

          {/* Condiciones Adicionales */}
          <Card>
            <CardHeader 
              title="Condiciones Adicionales"
              titleTypographyProps={{ variant: 'h6' }}
            />
            <CardContent>
              <TextField
                fullWidth
                label="Condiciones Adicionales"
                multiline
                rows={8}
                value={formData.condiciones_adicionales}
                onChange={handleChange('condiciones_adicionales')}
                placeholder="Observaciones adicionales"
              />
              
              <Box mt={2} display="flex" justifyContent="center">
                <Button 
                  variant="outlined" 
                  onClick={() => setShowCondiciones(!showCondiciones)}
                  startIcon={showCondiciones ? <ExpandMore /> : <Receipt />}
                >
                  {showCondiciones ? 'Ocultar Condiciones Obligatorias de Contratación' : 'Ver Condiciones Obligatorias de Contratación'}
                </Button>
              </Box>
              
              {showCondiciones && (
                <Box mt={2}>
                  <Typography variant="subtitle2" gutterBottom>Condiciones Obligatorias de Contratación:</Typography>
                  <Paper sx={{ p: 2, bgcolor: 'grey.50' }}>
                    <Typography variant="body2" style={{ whiteSpace: 'pre-line' }}>
                      {formData.condiciones_contratacion}
                    </Typography>
                  </Paper>
                </Box>
              )}
            </CardContent>
          </Card>

          {/* 5. Tarjeta de Resumen Financiero */}
          <Card>
            <CardHeader 
              title="Resumen Financiero"
              titleTypographyProps={{ variant: 'h6' }}
              avatar={<Receipt color="primary" />}
            />
            <CardContent>
              <Box display="flex" flexWrap="wrap" gap={3}>
                <Box flex="1 1 200px">
                  <Box textAlign="center" p={2} bgcolor="grey.50" borderRadius={1}>
                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                      Materiales
                    </Typography>
                    <Typography variant="h6" color="primary.main">
                      {formatCurrency(totals.materialesConDescuento, 'material')}
                    </Typography>
                    {totals.materialesConDescuento < totals.materialesUSD && (
                      <Typography variant="body2" color="text.secondary" fontWeight="medium">
                        Sin descuento: {formatCurrency(totals.materialesUSD, 'material')}
                      </Typography>
                    )}
                  </Box>
                </Box>



                <Box flex="1 1 200px">
                  <Box textAlign="center" p={2} bgcolor="grey.50" borderRadius={1}>
                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                      Total Instalación
                    </Typography>
                    <Typography variant="h6" color="primary.main">
                      {formData.descuento_instalacion > 0 ? formatCurrency(totals.instalacionConDescuento, 'instalacion') : formatCurrency(totals.instalacion, 'instalacion')}
                    </Typography>
                    {formData.descuento_instalacion > 0 && (
                      <Typography variant="body2" color="text.secondary" fontWeight="medium">
                        Sin descuento: {formatCurrency(totals.instalacion, 'instalacion')}
                      </Typography>
                    )}
                  </Box>
                </Box>

                <Box flex="1 1 200px">
                  <Box textAlign="center" p={2} bgcolor="grey.50" borderRadius={1}>
                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                      Total Fletes
                    </Typography>
                    <Typography variant="h6" color="primary.main">
                      {formData.descuento_fletes > 0 ? formatCurrency(totals.fletesConDescuento, 'flete') : formatCurrency(totals.fletes, 'flete')}
                    </Typography>
                    {formData.descuento_fletes > 0 && (
                      <Typography variant="body2" color="text.secondary" fontWeight="medium">
                        Sin descuento: {formatCurrency(totals.fletes, 'flete')}
                      </Typography>
                    )}
                  </Box>
                </Box>

                <Box flex="1 1 200px">
                  <Box textAlign="center" p={2} bgcolor="primary.main" borderRadius={1}>
                    <Typography variant="subtitle2" color="white" gutterBottom>
                      Total USD (con descuento)
                    </Typography>
                    <Typography variant="h6" color="white" fontWeight="bold">
                      {formatCurrency(totals.materialesConDescuento, 'material')}
                    </Typography>
                  </Box>
                </Box>

                <Box flex="1 1 200px">
                  <Box textAlign="center" p={2} bgcolor="success.main" borderRadius={1}>
                    <Typography variant="subtitle2" color="white" gutterBottom>
                      Total ARS (con descuento)
                    </Typography>
                    <Typography variant="h6" color="white" fontWeight="bold">
                      {formatCurrency(totals.instalacionConDescuento + totals.fletesConDescuento, 'instalacion')}
                    </Typography>
                  </Box>
                </Box>
              </Box>
            </CardContent>
          </Card>

          {/* Error */}
          {error && (
            <Alert severity="error" onClose={() => setError(null)}>
              {error}
            </Alert>
          )}

          {/* Botones de acción */}
          <Box display="flex" gap={2} justifyContent="flex-end">
            <Button
              type="button"
              variant="outlined"
              onClick={() => navigate('/budgets')}
              disabled={loading}
            >
              Cancelar
            </Button>
            
            <Button
              type="submit"
              variant="contained"
              startIcon={loading ? <CircularProgress size={16} /> : <Save />}
              disabled={loading}
              onClick={(e) => {
                // Asegurarnos de que el evento de submit se propague
                if (!e.defaultPrevented) {
                  e.currentTarget.form?.dispatchEvent(new Event('submit', { bubbles: true, cancelable: true }));
                }
              }}
            >
              {loading ? 'Creando...' : 'Crear Presupuesto Completo'}
            </Button>
          </Box>
        </Stack>
      </form>

      {/* Diálogos */}
      
      {/* Dialog para nuevo ambiente */}
      <Dialog
        open={environmentDialog}
        onClose={() => setEnvironmentDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Nuevo Ambiente</DialogTitle>
        <DialogContent>
          <Stack spacing={3} sx={{ mt: 1 }}>
            <TextField
              fullWidth
              label="Nombre del Ambiente"
              value={newEnvironment.nombre}
              onChange={(e) => setNewEnvironment(prev => ({
                ...prev,
                nombre: e.target.value
              }))}
              placeholder="ej: Sala de estar, Cocina, Dormitorio principal"
              required
            />
            
            <TextField
              fullWidth
              label="Descripción"
              multiline
              rows={3}
              value={newEnvironment.descripcion}
              onChange={(e) => setNewEnvironment(prev => ({
                ...prev,
                descripcion: e.target.value
              }))}
              placeholder="Descripción detallada del ambiente"
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEnvironmentDialog(false)}>
            Cancelar
          </Button>
          <Button
            onClick={handleAddEnvironment}
            variant="contained"
            disabled={!newEnvironment.nombre.trim()}
          >
            Agregar Ambiente
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog para nuevo material */}
      <Dialog
        open={materialDialog}
        onClose={() => {
          setMaterialDialog(false);
          setSelectedEnvironmentIndex(null);
        }}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Agregar Material al Ambiente</DialogTitle>
        <DialogContent>
          <Stack spacing={3} sx={{ mt: 1 }}>
            {/* Selección de producto existente primero */}
            <Autocomplete
              options={products}
              getOptionLabel={(option: Product) => `${option.codigo} ${option.sku ? `- ${option.sku}` : ''} ${option.color ? `(${option.color})` : ''}`}
              value={!newMaterial.es_in_situ ? (newMaterial.product || null) : null}
              onChange={(event, newValue) => handleProductSelect(newValue)}
              disabled={newMaterial.es_in_situ}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Seleccionar Producto *"
                  placeholder="Buscar por descripción, código, color o grupo..."
                />
              )}
              renderOption={(props: any, option: Product) => (
                <Box component="li" {...props}>
                  <Box>
                    <Box display="flex" alignItems="center" gap={1} mb={0.5}>
                      <Typography variant="body2" fontWeight="bold">
                        {option.descripcion || option.codigo}
                      </Typography>
                      {option.descuento === -1 && (
                        <Chip label="Outlet" size="small" color="warning" />
                      )}
                    </Box>
                    <Typography variant="caption" color="text.secondary">
                      Código: {option.codigo} | Grupo: {option.grupo || 'Sin grupo'} | Color: {option.color || 'Sin color'} | Precio: {formatCurrency(option.precio_unitario || 0)}
                    </Typography>
                    {option.largo && option.ancho && (
                      <Typography variant="caption" color="text.secondary" display="block">
                        Dimensiones: {option.largo} × {option.ancho} {option.espesor ? `× ${option.espesor}` : ''}
                      </Typography>
                    )}
                  </Box>
                </Box>
              )}
              filterOptions={(options: Product[], { inputValue }: { inputValue: string }) => {
                if (!inputValue) return options;
                const lowerInput = inputValue.toLowerCase();
                return options.filter((option: Product) =>
                  (option.descripcion && option.descripcion.toLowerCase().includes(lowerInput)) ||
                  (option.grupo && option.grupo.toLowerCase().includes(lowerInput)) ||
                  option.codigo.toLowerCase().includes(lowerInput) ||
                  (option.color && option.color.toLowerCase().includes(lowerInput)) ||
                  (option.sku && option.sku.toLowerCase().includes(lowerInput))
                );
              }}
            />

            {/* Toggle para producto manual (abajo del selector) */}
            <Box>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={newMaterial.es_in_situ}
                    onChange={(e) => setNewMaterial(prev => ({
                      ...prev,
                      es_in_situ: e.target.checked,
                      product_id: undefined,
                      product: undefined,
                      descripcion: '',
                      codigo: '',
                      precio_unitario: 0,
                      cantidad_calculada: 0,
                      metros_cuadrados: 1,
                      usar_cantidad_manual: false,
                      con_desperdicio: true,
                      con_estructura: false,
                      observaciones: ''
                    }))}
                  />
                }
                label="Ingresar producto manual (no existente en catálogo)"
              />
              
              {newMaterial.es_in_situ && (
                <Alert severity="warning" sx={{ mt: 1 }}>
                  Este producto no se creará en el Catálogo, solo aparecerá en este presupuesto
                </Alert>
              )}
            </Box>

            {!newMaterial.es_in_situ ? (
              // Selección de producto existente - opciones adicionales
              <>

                {newMaterial.product && (
                  <>
                    {/* Opciones de desperdicio y estructura */}
                    <Box>
                      <Typography variant="subtitle2" gutterBottom>
                        Opciones del Producto:
                      </Typography>
                      <Stack spacing={1}>
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={newMaterial.con_desperdicio || false}
                              onChange={(e) => handleDesperdicioChange(e.target.checked)}
                            />
                          }
                          label="Incluir desperdicio (+10%)"
                        />
                        {newMaterial.product.estructura_m2 && (
                          <FormControlLabel
                            control={
                              <Checkbox
                                checked={newMaterial.con_estructura || false}
                                onChange={(e) => setNewMaterial(prev => ({
                                  ...prev,
                                  con_estructura: e.target.checked
                                }))}
                              />
                            }
                            label="Incluir estructura"
                          />
                        )}
                      </Stack>
                    </Box>

                    {/* Campos de m² y cantidad que se completan mutuamente */}
                    <Box>
                      <Typography variant="subtitle2" gutterBottom>
                        Cálculo de Cantidad:
                      </Typography>
                      <Box display="flex" gap={2}>
                        <TextField
                          fullWidth
                          label="Metros Cuadrados"
                          type="number"
                          value={newMaterial.metros_cuadrados || 1}
                          onChange={(e) => {
                            if (!newMaterial.usar_cantidad_manual) {
                              handleMetrosCuadradosChange(parseFloat(e.target.value) || 1);
                            } else {
                              setNewMaterial(prev => ({
                                ...prev,
                                metros_cuadrados: parseFloat(e.target.value) || 1
                              }));
                            }
                          }}
                          placeholder="Área a cubrir en m²"
                          inputProps={{ min: 0.1, step: 0.1 }}
                        />

                        <TextField
                          fullWidth
                          label="Cantidad (unidades)"
                          type="number"
                          value={newMaterial.usar_cantidad_manual ? (newMaterial.cantidad_manual || 0) : (newMaterial.cantidad_calculada || 0)}
                          onChange={(e) => {
                            const cantidad = parseFloat(e.target.value) || 0;
                            setNewMaterial(prev => ({ ...prev, usar_cantidad_manual: true }));
                            handleCantidadManualChange(cantidad);
                          }}
                          placeholder="Cantidad específica"
                          inputProps={{ min: 0, step: 1 }}
                        />
                      </Box>
                    </Box>

                    {newMaterial.cantidad_calculada && newMaterial.cantidad_calculada > 0 && !newMaterial.usar_cantidad_manual && (
                      <Alert severity="info">
                        <Typography variant="body2">
                          Cantidad calculada automáticamente: <strong>{newMaterial.cantidad_calculada} unidades</strong>
                          {newMaterial.con_desperdicio && <span> (incluye 10% desperdicio)</span>}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          Basado en {newMaterial.metros_cuadrados}m² ÷ ({newMaterial.product.largo}m × {newMaterial.product.ancho_util ?? newMaterial.product.ancho}m{newMaterial.product.ancho_util ? ' útil' : ''} por unidad)
                        </Typography>
                      </Alert>
                    )}

                    {newMaterial.usar_cantidad_manual && newMaterial.cantidad_manual && newMaterial.product && (
                      <Alert severity="success">
                        <Typography variant="body2">
                          Metros cuadrados calculados automáticamente: <strong>{newMaterial.metros_cuadrados}m²</strong>
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          Basado en {newMaterial.cantidad_manual} unidades × ({newMaterial.product.largo}m × {newMaterial.product.ancho_util ?? newMaterial.product.ancho}m{newMaterial.product.ancho_util ? ' útil' : ''} por unidad)
                          {newMaterial.con_desperdicio && <span> (considerando desperdicio incluido)</span>}
                        </Typography>
                      </Alert>
                    )}

                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={newMaterial.usar_cantidad_manual || false}
                          onChange={(e) => {
                            const useManual = e.target.checked;
                            setNewMaterial(prev => ({
                              ...prev,
                              usar_cantidad_manual: useManual,
                              unidades: useManual ? (prev.cantidad_manual || 0) : (prev.cantidad_calculada || 0)
                            }));
                          }}
                        />
                      }
                      label="Usar cantidad manual (deshabilita cálculo automático)"
                    />

                    {/* Campo de observaciones */}
                    <TextField
                      fullWidth
                      label="Observaciones"
                      value={newMaterial.observaciones || ''}
                      onChange={(e) => setNewMaterial(prev => ({
                        ...prev,
                        observaciones: e.target.value.slice(0, 100) // Limitar a 100 caracteres
                      }))}
                      placeholder="Observaciones sobre este material"
                      multiline
                      rows={2}
                      inputProps={{ maxLength: 100 }}
                      helperText={`${(newMaterial.observaciones || '').length}/100 caracteres`}
                    />

                    {/* Vista previa de estructura si está marcada */}
                    {newMaterial.con_estructura && newMaterial.product.estructura_m2 && (
                      <Alert severity="info">
                        <Typography variant="subtitle2" gutterBottom>
                          ⚠️ Estructura incluida
                        </Typography>
                        <Typography variant="body2" gutterBottom>
                          {newMaterial.product.grupo === 'PERFIL'
                            ? `Este producto incluye materiales de estructura que se agregarán automáticamente calculados por unidad (total unidades: ${newMaterial.unidades || 1}).`
                            : `Este producto incluye materiales de estructura que se agregarán automáticamente calculados por metro cuadrado (total m²: ${newMaterial.metros_cuadrados || 1}).`}
                        </Typography>
                        <Box component="ul" sx={{ margin: 0, paddingLeft: 2 }}>
                          {newMaterial.product.estructura_m2.split(/[;\n]+/).map((entry, index) => {
                            const trimmedEntry = entry.trim();
                            if (!trimmedEntry) return null;
                            try {
                              const [codigo, cantidadPorM2Str] = trimmedEntry.split(',');
                              const codigoTrimmed = codigo.trim();
                              const cantidadPorM2 = parseFloat(cantidadPorM2Str.trim());
                              const structureProduct = products.find((p: Product) => p.codigo === codigoTrimmed);
                              let cantidadBase, cantidadTotal, cantidadFinal, detalleCalculo;
                              if (newMaterial.product && newMaterial.product.grupo === 'PERFIL') {
                                cantidadBase = newMaterial.unidades || 1;
                                cantidadTotal = cantidadPorM2 * cantidadBase;
                                cantidadFinal = Math.ceil(cantidadTotal);
                                detalleCalculo = `(${cantidadPorM2}/unidad × ${cantidadBase} unidades)`;
                              } else {
                                cantidadBase = newMaterial.metros_cuadrados || 1;
                                cantidadTotal = cantidadPorM2 * cantidadBase;
                                cantidadFinal = Math.ceil(cantidadTotal);
                                detalleCalculo = `(${cantidadPorM2}/m² × ${cantidadBase}m²)`;
                              }
                              return (
                                <Typography 
                                  key={index} 
                                  component="li" 
                                  variant="caption" 
                                  color="text.secondary"
                                >
                                  <strong>{codigoTrimmed}</strong>
                                  {structureProduct && ` (${structureProduct.sku || 'Sin SKU'})`}: 
                                  {' '}{cantidadFinal} unidades
                                  <span style={{ color: '#666' }}>
                                    {' '}{detalleCalculo}
                                  </span>
                                  <span style={{ color: '#888', fontSize: '0.8em' }}>
                                    {' '}(sin desperdicio)
                                  </span>
                                </Typography>
                              );
                            } catch {
                              return null;
                            }
                          })}
                        </Box>
                      </Alert>
                    )}
                  </>
                )}
              </>
            ) : (
              // Creación de material in situ
              <>
                <TextField
                  fullWidth
                  label="Código *"
                  value={newMaterial.codigo}
                  onChange={(e) => setNewMaterial(prev => ({
                    ...prev,
                    codigo: e.target.value
                  }))}
                  placeholder="Código único del material"
                  required
                />

                <TextField
                  fullWidth
                  label="Descripción *"
                  value={newMaterial.descripcion}
                  onChange={(e) => setNewMaterial(prev => ({
                    ...prev,
                    descripcion: e.target.value
                  }))}
                  placeholder="Descripción detallada del material"
                  required
                />
                
                <Box display="flex" gap={2}>
                  <TextField
                    fullWidth
                    label="Unidades"
                    type="number"
                    value={newMaterial.unidades}
                    onChange={(e) => setNewMaterial(prev => ({
                      ...prev,
                      unidades: parseFloat(e.target.value) || 0
                    }))}
                    placeholder="Cantidad"
                  />

                  <TextField
                    fullWidth
                    label="Precio Unitario"
                    type="number"
                    value={newMaterial.precio_unitario}
                    onChange={(e) => setNewMaterial(prev => ({
                      ...prev,
                      precio_unitario: parseFloat(e.target.value) || 0
                    }))}
                    placeholder="Precio por unidad"
                  />

                  <FormControl fullWidth>
                    <InputLabel>Moneda</InputLabel>
                    <Select
                      value={newMaterial.currency}
                      onChange={(e) => setNewMaterial(prev => ({
                        ...prev,
                        currency: e.target.value as 'USD' | 'ARS'
                      }))}
                      label="Moneda"
                    >
                      <MenuItem value="USD">USD</MenuItem>
                      <MenuItem value="ARS">ARS</MenuItem>
                    </Select>
                  </FormControl>
                </Box>
              </>
            )}

            {/* Resumen del cálculo */}
            {(newMaterial.unidades > 0 && newMaterial.precio_unitario > 0) && (
              <Box sx={{ p: 2, bgcolor: 'grey.100', borderRadius: 1 }}>
                <Typography variant="subtitle2" gutterBottom>
                  Resumen del Material
                </Typography>
                <Box display="flex" justifyContent="space-between" alignItems="center">
                  <Typography variant="body2">
                    {newMaterial.unidades} unidades × {formatCurrency(newMaterial.precio_unitario)}
                  </Typography>
                  <Typography variant="h6" color="primary">
                    {formatCurrency(newMaterial.unidades * newMaterial.precio_unitario)}
                  </Typography>
                </Box>
                {newMaterial.product && (
                  <Typography variant="caption" color="text.secondary" display="block">
                    Producto: {newMaterial.product.codigo} {newMaterial.product.sku ? `(${newMaterial.product.sku})` : ''}
                  </Typography>
                )}
              </Box>
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => {
            setMaterialDialog(false);
            setSelectedEnvironmentIndex(null);
          }}>
            Cancelar
          </Button>
          <Button
            onClick={handleAddMaterial}
            variant="contained"
            disabled={
              newMaterial.es_in_situ 
                ? (!newMaterial.codigo.trim() || !newMaterial.descripcion.trim())
                : !newMaterial.product_id
            }
          >
            Agregar Material
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog para nueva instalación */}
      <Dialog
        open={installationDialog}
        onClose={() => {
          setInstallationDialog(false);
          setSelectedEnvironmentIndex(null);
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Instalación del Ambiente</DialogTitle>
        <DialogContent>
          <Stack spacing={3} sx={{ mt: 1 }}>
            {/* Selector de ambiente */}
            <FormControl fullWidth required>
              <InputLabel>Seleccionar Ambiente</InputLabel>
              <Select
                value={selectedEnvironmentIndex !== null ? selectedEnvironmentIndex : ''}
                onChange={(e) => setSelectedEnvironmentIndex(Number(e.target.value))}
                label="Seleccionar Ambiente"
              >
                {/* Opción para instalaciones generales */}
                <MenuItem value={GENERAL_INSTALLATIONS_INDEX}>
                  <em>Instalaciones Generales (sin ambiente específico)</em>
                </MenuItem>
                <Divider />
                {formData.environments
                  .filter(env => env.nombre !== GENERAL_INSTALLATIONS_ENV_NAME)
                  .map((env, index) => {
                    // Calcular el índice real considerando que filtramos el ambiente general
                    const realIndex = formData.environments.findIndex(e => e.nombre === env.nombre);
                    return (
                      <MenuItem 
                        key={realIndex} 
                        value={realIndex}
                      >
                        {env.nombre}
                      </MenuItem>
                    );
                  })}
              </Select>
              {selectedEnvironmentIndex !== null && selectedEnvironmentIndex !== GENERAL_INSTALLATIONS_INDEX && formData.environments[selectedEnvironmentIndex] && (
                <Typography variant="caption" color="text.secondary">
                  Área total del ambiente: {formData.environments[selectedEnvironmentIndex].materials.reduce(
                    (total, mat) => total + (mat.metros_cuadrados || 0), 0
                  ).toFixed(2)} m²
                </Typography>
              )}
              {selectedEnvironmentIndex === GENERAL_INSTALLATIONS_INDEX && (
                <Typography variant="caption" color="text.secondary">
                  Esta instalación no estará asociada a ningún ambiente específico
                </Typography>
              )}
            </FormControl>

            {/* Toggle para elegir modo de instalación */}
            <Box sx={{ display: 'flex', justifyContent: 'center' }}>
              <ToggleButtonGroup
                value={installationMode}
                exclusive
                onChange={(e, newMode) => {
                  if (newMode !== null) {
                    setInstallationMode(newMode);
                    // Limpiar campos al cambiar de modo
                    setNewInstallation({
                      cantidad_manual: 1,
                      currency: 'ARS'
                    });
                  }
                }}
                size="small"
              >
                <ToggleButton value="catalog">
                  Desde Catálogo
                </ToggleButton>
                <ToggleButton value="custom">
                  Personalizada
                </ToggleButton>
              </ToggleButtonGroup>
            </Box>

            {/* MODO CATÁLOGO */}
            {installationMode === 'catalog' && (
              <>
                {/* Buscador de servicios de instalación */}
                <Autocomplete
                  fullWidth
                  options={installationServices}
                  getOptionLabel={(option: InstallationService) => `${option.trabajo} (${option.unidad})`}
                  value={newInstallation.installation_service || null}
                  onChange={(event, newValue) => handleInstallationServiceSelect(newValue)}
                  loading={loadingInstallationServices}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Seleccionar Servicio de Instalación *"
                      placeholder="Buscar por nombre de trabajo..."
                      InputProps={{
                        ...params.InputProps,
                        endAdornment: (
                          <>
                            {loadingInstallationServices ? <CircularProgress color="inherit" size={20} /> : null}
                            {params.InputProps.endAdornment}
                          </>
                        ),
                      }}
                    />
                  )}
                  renderOption={(props: any, option: InstallationService) => (
                    <Box component="li" {...props}>
                      <Box>
                        <Typography variant="body2" fontWeight="bold">
                          {option.trabajo}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          Unidad: {option.unidad}
                          {option.precio_standard && ` | Estándar: ${formatInstallationCurrency(option.precio_standard, option.moneda_standard)}`}
                          {option.precio_complejo && ` | Complejo: ${formatInstallationCurrency(option.precio_complejo, option.moneda_compleja)}`}
                        </Typography>
                        {option.descripcion && (
                          <Typography variant="caption" color="text.secondary" display="block">
                            {option.descripcion}
                          </Typography>
                        )}
                      </Box>
                    </Box>
                  )}
                  filterOptions={(options: InstallationService[], { inputValue }: { inputValue: string }) => {
                    if (!inputValue) return options;
                    const lowerInput = inputValue.toLowerCase();
                    return options.filter((option: InstallationService) =>
                      option.trabajo.toLowerCase().includes(lowerInput) ||
                      (option.descripcion && option.descripcion.toLowerCase().includes(lowerInput)) ||
                      option.unidad.toLowerCase().includes(lowerInput)
                    );
                  }}
                />

                {/* Selección de tipo de instalación si hay servicio seleccionado */}
                {newInstallation.installation_service && (
                  <FormControl fullWidth>
                    <InputLabel>Tipo de Instalación</InputLabel>
                    <Select
                      value={newInstallation.tipo_instalacion || ''}
                      onChange={(e) => setNewInstallation(prev => ({
                        ...prev,
                        tipo_instalacion: e.target.value as TipoInstalacion
                      }))}
                      label="Tipo de Instalación"
                    >
                      {newInstallation.installation_service.precio_standard && (
                        <MenuItem value="standard">
                          Estándar - {formatInstallationCurrency(newInstallation.installation_service.precio_standard, newInstallation.installation_service.moneda_standard)}
                        </MenuItem>
                      )}
                      {newInstallation.installation_service.precio_complejo && (
                        <MenuItem value="compleja">
                          Compleja - {formatInstallationCurrency(newInstallation.installation_service.precio_complejo, newInstallation.installation_service.moneda_compleja)}
                        </MenuItem>
                      )}
                      <MenuItem value="custom">Precio personalizado</MenuItem>
                    </Select>
                  </FormControl>
                )}

                {/* Campo de cantidad manual - CATÁLOGO */}
                <TextField
                  fullWidth
                  label="Cantidad"
                  type="number"
                  value={newInstallation.cantidad_manual}
                  onChange={(e) => setNewInstallation(prev => ({
                    ...prev,
                    cantidad_manual: parseFloat(e.target.value) || 1
                  }))}
                  placeholder="Cantidad a instalar"
                  inputProps={{ min: 0.1, step: 0.1 }}
                  InputLabelProps={{ shrink: true }}
                  helperText={newInstallation.installation_service 
                    ? `Unidad: ${newInstallation.installation_service.unidad}`
                    : "Cantidad de unidades a instalar"
                  }
                  required
                />

                {/* Campo de precio - precargado con valor calculado si existe */}
                {newInstallation.tipo_instalacion && newInstallation.installation_service && (
                  <TextField
                    fullWidth
                    label="Precio por unidad"
                    type="number"
                    value={newInstallation.mano_obra_m2 || calculateInstallationPrice()}
                    onChange={(e) => setNewInstallation(prev => ({
                      ...prev,
                      mano_obra_m2: parseFloat(e.target.value) || 0
                    }))}
                    placeholder="Precio por unidad"
                    InputProps={{
                      endAdornment: <span style={{ marginLeft: '8px', color: '#666' }}>ARS</span>
                    }}
                    helperText={newInstallation.tipo_instalacion === 'custom' ? 'Ingresa el precio personalizado' : 'Campo precargado con precio del catálogo (editable)'}
                  />
                )}
              </>
            )}

            {/* MODO PERSONALIZADO */}
            {installationMode === 'custom' && (
              <>
                {/* Nombre del trabajo */}
                <TextField
                  fullWidth
                  label="Nombre del trabajo *"
                  value={newInstallation.trabajo || ''}
                  onChange={(e) => setNewInstallation(prev => ({
                    ...prev,
                    trabajo: e.target.value,
                    tipo_instalacion: 'custom' as TipoInstalacion
                  }))}
                  placeholder="Ej: Instalación de vidrio templado"
                  required
                />

                {/* Unidad y Cantidad en la misma fila */}
                <Box sx={{ display: 'flex', gap: 2 }}>
                  <FormControl sx={{ minWidth: 120 }}>
                    <InputLabel>Unidad</InputLabel>
                    <Select
                      value={newInstallation.unidad || ''}
                      onChange={(e) => setNewInstallation(prev => ({
                        ...prev,
                        unidad: e.target.value
                      }))}
                      label="Unidad"
                    >
                      <MenuItem value="m2">m2</MenuItem>
                      <MenuItem value="ml">ml (metro lineal)</MenuItem>
                      <MenuItem value="unit">Unidad</MenuItem>
                      <MenuItem value="global">Global</MenuItem>
                      <MenuItem value="hora">Hora</MenuItem>
                    </Select>
                  </FormControl>
                  <TextField
                    fullWidth
                    label="Cantidad"
                    type="number"
                    value={newInstallation.cantidad_manual}
                    onChange={(e) => setNewInstallation(prev => ({
                      ...prev,
                      cantidad_manual: parseFloat(e.target.value) || 1
                    }))}
                    placeholder="Cantidad"
                    inputProps={{ min: 0.1, step: 0.1 }}
                    InputLabelProps={{ shrink: true }}
                    required
                  />
                </Box>

                {/* Precio y Moneda en la misma fila */}
                <Box sx={{ display: 'flex', gap: 2 }}>
                  <TextField
                    fullWidth
                    label="Precio por unidad *"
                    type="number"
                    value={newInstallation.mano_obra_m2 || ''}
                    onChange={(e) => setNewInstallation(prev => ({
                      ...prev,
                      mano_obra_m2: parseFloat(e.target.value) || 0
                    }))}
                    placeholder="Precio"
                    inputProps={{ min: 0, step: 0.01 }}
                    InputLabelProps={{ shrink: true }}
                    required
                  />
                  <FormControl sx={{ minWidth: 100 }}>
                    <InputLabel>Moneda</InputLabel>
                    <Select
                      value={newInstallation.currency}
                      onChange={(e) => setNewInstallation(prev => ({
                        ...prev,
                        currency: e.target.value as 'USD' | 'ARS'
                      }))}
                      label="Moneda"
                    >
                      <MenuItem value="ARS">ARS</MenuItem>
                      <MenuItem value="USD">USD</MenuItem>
                    </Select>
                  </FormControl>
                </Box>
              </>
            )}

            {/* Campo de descripción editable - COMÚN A AMBOS MODOS */}
            <TextField
              fullWidth
              label="Descripción"
              multiline
              rows={2}
              value={newInstallation.descripcion_trabajo || ''}
              onChange={(e) => setNewInstallation(prev => ({
                ...prev,
                descripcion_trabajo: e.target.value
              }))}
              placeholder="Descripción del trabajo de instalación"
              helperText="Descripción del servicio (opcional)"
            />
            
            {/* Resumen de costo */}
            {selectedEnvironmentIndex !== null && (
              <Alert severity="info">
                <Typography variant="subtitle2" gutterBottom>
                  Resumen de Instalación
                </Typography>
                <Typography variant="body2">
                  Servicio: {installationMode === 'catalog' 
                    ? (newInstallation.installation_service?.trabajo || 'No seleccionado')
                    : (newInstallation.trabajo || 'Sin nombre')}
                </Typography>
                <Typography variant="body2">
                  Cantidad: {newInstallation.cantidad_manual} {
                    installationMode === 'catalog' 
                      ? (newInstallation.installation_service?.unidad || 'unidades')
                      : (newInstallation.unidad || '')
                  }
                </Typography>
                <Typography variant="body2">
                  Precio unitario: {formatCurrency(
                    installationMode === 'catalog' ? calculateInstallationPrice() : (newInstallation.mano_obra_m2 || 0), 
                    'instalacion'
                  )}
                </Typography>
                <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                  Total: {formatCurrency(
                    installationMode === 'catalog' 
                      ? calculateInstallationPrice() * newInstallation.cantidad_manual
                      : (newInstallation.unidad?.toLowerCase() === 'global' 
                          ? (newInstallation.mano_obra_m2 || 0) 
                          : (newInstallation.mano_obra_m2 || 0) * newInstallation.cantidad_manual), 
                    'instalacion'
                  )}
                </Typography>
              </Alert>
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => {
            setInstallationDialog(false);
            setSelectedEnvironmentIndex(null);
            setInstallationMode('catalog');
          }}>
            Cancelar
          </Button>
          <Button
            onClick={handleAddInstallation}
            variant="contained"
            disabled={
              selectedEnvironmentIndex === null || 
              newInstallation.cantidad_manual <= 0 ||
              (installationMode === 'catalog' && !newInstallation.installation_service_id) ||
              (installationMode === 'custom' && (!newInstallation.trabajo?.trim() || !newInstallation.mano_obra_m2))
            }
          >
            Agregar Instalación
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog para nuevo flete */}
      <Dialog
        open={freightDialog}
        onClose={() => setFreightDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Nuevo Flete</DialogTitle>
        <DialogContent>
          <Stack spacing={3} sx={{ mt: 1 }}>
            <FormControl fullWidth required>
              <InputLabel>Tipo Flete</InputLabel>
              <Select
                value={newFreight.tipo_envio}
                onChange={(e) => setNewFreight(prev => ({
                  ...prev,
                  tipo_envio: e.target.value
                }))}
                label="Tipo Flete"
              >
                <MenuItem value="Flete y acarreo PB">Flete y acarreo PB</MenuItem>
                <MenuItem value="Flete y acarreo especial">Flete y acarreo especial</MenuItem>
                <MenuItem value="Flete sin acarreo">Flete sin acarreo</MenuItem>
              </Select>
            </FormControl>

            {/* Campo de descripción detallada opcional */}
            <TextField
              fullWidth
              label="Descripción detallada (opcional)"
              multiline
              rows={2}
              value={newFreight.descripcion_detalle || ''}
              onChange={(e) => setNewFreight(prev => ({
                ...prev,
                descripcion_detalle: e.target.value
              }))}
              placeholder="Detalles del flete: ubicación, condiciones especiales, etc."
              inputProps={{ maxLength: 150 }}
              helperText={`${(newFreight.descripcion_detalle || '').length}/150 caracteres`}
            />
            
            <TextField
              fullWidth
              label="Monto"
              type="number"
              value={newFreight.monto}
              onChange={(e) => setNewFreight(prev => ({
                ...prev,
                monto: parseFloat(e.target.value) || 0
              }))}
              placeholder="Monto del flete"
              InputProps={{
                endAdornment: <span style={{ marginLeft: '8px', color: '#666' }}>AR$</span>
              }}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setFreightDialog(false)}>
            Cancelar
          </Button>
          <Button
            onClick={handleAddFreight}
            variant="contained"
            disabled={!newFreight.tipo_envio.trim()}
          >
            Agregar Flete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Alerta de stock */}
      {showAlertStock && alertStock && (
        <Alert
          severity="warning"
          sx={{ mb: 2 }}
          action={
            <IconButton
              aria-label="close"
              color="inherit"
              size="small"
              onClick={() => {
                setShowAlertStock(false);
                setAlertStock(null);
              }}
            >
              <CloseIcon fontSize="inherit" />
            </IconButton>
          }
        >
          {alertStock}
        </Alert>
      )}

      <Snackbar
        open={showAlertStock && !!alertStock}
        autoHideDuration={null}
        onClose={() => {
          setShowAlertStock(false);
          setAlertStock(null);
        }}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        sx={{
          '& .MuiSnackbarContent-root': {
            minWidth: '400px',
            justifyContent: 'center',
            alignItems: 'center',
            left: '50%',
            top: '50%',
            transform: 'translate(-50%, -50%)',
            position: 'fixed',
            zIndex: 1500,
            fontSize: '1.2rem',
            fontWeight: 'bold',
            boxShadow: 8,
          }
        }}
      >
        <Alert
          severity="error"
          action={
            <IconButton
              aria-label="close"
              color="inherit"
              size="small"
              onClick={() => {
                setShowAlertStock(false);
                setAlertStock(null);
              }}
            >
              <CloseIcon fontSize="inherit" />
            </IconButton>
          }
          sx={{
            width: '100%',
            fontSize: '1.2rem',
            fontWeight: 'bold',
            textAlign: 'center',
            bgcolor: '#d32f2f', // Rojo fuerte
            color: '#fff',
            border: '2px solid #b71c1c',
            boxShadow: 8,
          }}
        >
          {alertStock}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default BudgetFormNew;
